<template>
  <div class="linkook-live-detail-wrapper">
    <div class="detail-container" v-if="live">
      <!-- 모바일 전용 헤더 -->
      <div class="detail-header mobile-only">
        <button class="back-arrow-btn" @click="$router.push('/')" title="뒤로가기">
          ←
        </button>
        <button class="share-btn" @click="showShareModal = true" title="공유하기">
          <img src="@/assets/images/ios_share.png" alt="공유" class="share-icon" />
        </button>
      </div>

      <div class="detail-content">
        <div>
          <div class="detail-poster-container">
            <div class="poster-blur-background" :style="posterBackgroundStyle"></div>
            <div class="poster-wrapper">
              <!-- 데스크톱 스켈레톤 UI -->
              <div v-if="!posterLoaded" class="poster-skeleton desktop-only">
                <div class="skeleton-shimmer"></div>
              </div>
              <img :src="live.img" :alt="live.title" class="detail-poster" :class="{ 'poster-loaded': posterLoaded }"
                @load="onPosterLoad" @click="openLightbox(live.img)" />
            </div>

            <button class="likeBtnOverlay" style="font-size: 13px; line-height: 1" @click.stop="handleLikeClick()">
              <img v-if="liked" class="like-heart liked" src="@/assets/images/heart.png" />
              <img v-else class="like-heart" src="@/assets/images/heart_lightgray.png" />
            </button>
          </div>
          <div v-if="(live.linkookReservation || live.reservationLink) && calculateDaysLeft(live.date) !== '종료'"
            class="reserve-btn mobile-reserve-btn">
            <button :class="[
              'reserve-btn-inner',
              {
                'reservation-confirm':
                  userReservation,
                'linkook-reserve':
                  live.linkookReservation && (isReservationOpened || isAdmin) &&
                  (!isTicketSoldOut || userReservation) &&
                  !isReservationClosed &&
                  !userReservation,
                'reservation-wait':
                  live.linkookReservation &&
                  !isAdmin &&
                  !isReservationOpened &&
                  !isReservationClosed &&
                  !isTicketSoldOut &&
                  !userReservation,
                'sold-out':
                  live.linkookReservation &&
                  isTicketSoldOut &&
                  !userReservation,
                'reservation-closed':
                  live.linkookReservation &&
                  (isReservationClosed) &&
                  !isTicketSoldOut &&
                  !userReservation,
              },
            ]" @click="goToReservationLink"
              :disabled="live.linkookReservation && !isAdmin && (!isReservationOpened || isReservationClosed) && !userReservation">
              {{ getReservationButtonText() }}
            </button>
          </div>
        </div>
        <!-- 임시저장 상태 안내 -->
        <div v-if="live.status === 'draft'" class="draft-notice" @click="editLive">
          <div class="draft-notice-icon">📝</div>
          <div class="draft-notice-text">
            <span class="draft-notice-label">임시저장 상태입니다</span>
            <p class="draft-notice-desc">등록을 완료해야 게시 승인을 받을 수 있습니다.</p>
          </div>
        </div>
        <!-- 승인 대기 안내 (등록 완료 + 미승인) -->
        <div v-else-if="!live.approved" class="approval-pending-notice">
          <div class="approval-pending-icon">⏳</div>
          <div class="approval-pending-text">
            <span class="approval-pending-label">승인 대기중입니다</span>
            <p class="approval-pending-desc">관리자 승인 후 메인에 노출됩니다.</p>
          </div>
        </div>
        <div class="detail-info">
          <div class="detail-title">
            <div class="title-left">
              <h1>{{ live.title }}</h1>
              <a v-if="live.link" class="detail-more-link" @click="goToLink">자세히보기</a>
            </div>
            <div v-if="hasManagePermission || isAdmin" class="manage-btn-wrapper" @focusout="onManageWrapperFocusout">
              <button class="manage-settings-btn" @click="showManageDropdown = !showManageDropdown" title="관리 메뉴">
                <img src="@/assets/images/settings.png" alt="관리" class="settings-icon" />
              </button>
              <div v-if="showManageDropdown" class="manage-dropdown">
                <button class="manage-dropdown-item" @click="editLive(); showManageDropdown = false">
                  수정하기
                </button>
                <button class="manage-dropdown-item" @click="openMemberManage(); showManageDropdown = false">
                  권한관리
                </button>
                <button class="manage-dropdown-item" v-if="live.linkookReservation"
                  @click="goToReservationManage(); showManageDropdown = false">
                  예매폼관리
                </button>
                <button class="manage-dropdown-item" v-if="live.linkookReservation"
                  @click="goToReservationList(); showManageDropdown = false">
                  예매관리
                </button>
                <button class="manage-dropdown-item manage-dropdown-item--danger"
                  @click="deleteLive(); showManageDropdown = false" :disabled="deleteLoading">
                  {{ deleteLoading ? "삭제중..." : "삭제" }}
                </button>
              </div>
            </div>
          </div>
          <div class="info-table">
            <div class="info-row" v-if="live.type">
              <div class="info-label">공연타입</div>
              <div class="info-value type-with-icon">
                <img :src="typeIconUrl(live.type)" :alt="live.type" class="type-icon" />
                <span>{{ (live.type || "").toUpperCase() }}</span>
              </div>
            </div>
            <div class="info-row" v-if="live.place">
              <div class="info-label">장소</div>
              <div class="info-value place-with-map">
                <span>{{ live.place }}</span>
                <a v-if="venueMapLink" @click="goToVenueMap" class="map-view-link" title="지도에서 보기" href="#">
                  지도보기
                </a>
              </div>
            </div>
            <div class="info-row" v-if="live.date">
              <div class="info-label">공연일시</div>
              <div class="info-value">
                <p v-if="live.entryTime">입장 | {{ live.entryTime }}</p>
                <p>공연 | {{ live.date }}</p>
              </div>
            </div>

            <!-- 티켓 가격 정보 -->
            <div class="info-row" v-if="live.linkookPrice || live.advancePrice || live.doorPrice">
              <div class="info-label">티켓가격</div>
              <div class="info-value ticket-price-info">
                <p v-if="live.linkookReservation && live.linkookPrice" class="ticket-price linkook-price">
                  링꾹예매 | {{ live.linkookPrice }}
                  <span v-if="isTicketSoldOut" class="soldout-badge">매진</span>
                  <span v-else-if="!isAdmin && !isReservationOpened" class="waiting-badge">
                    예매대기<span v-if="reservationCountdownText"> · {{ reservationCountdownText }}</span>
                  </span>
                </p>
                <p v-if="live.advancePrice" class="ticket-price">
                  사전예매 | {{ live.advancePrice }}
                </p>
                <p v-if="live.doorPrice" class="ticket-price">
                  현장구매 | {{ live.doorPrice }}
                </p>
              </div>
            </div>
            <!-- 라인업 정보 -->
            <div class="info-row" v-if="lineupDisplayText">
              <div class="info-label">라인업</div>
              <div class="info-value">
                {{ lineupDisplayText }}
              </div>
            </div>
            <!-- 함께한 사람들 정보 -->
            <!-- <div class="info-row" v-if="crewDisplayText">
              <div class="info-label">함께한 사람들</div>
              <div class="info-value">
                {{ crewDisplayText }}
              </div>
            </div> -->
            <!-- 포스터 작가 정보 -->
            <div class="info-row" v-if="live.posterCreatorNickname || live.posterDetailLink">
              <div class="info-label">포스터 작가</div>
              <div class="info-value place-with-map">
                <span>{{ live.posterCreatorNickname }}</span>
                <a v-if="live.posterDetailLink" :href="live.posterDetailLink" target="_blank" rel="noopener noreferrer"
                  class="map-view-link" title="자세히보기">
                  자세히보기
                </a>
              </div>
            </div>
            <!-- 예매 기간 정보 (링꾹 예매인 경우) -->
            <div class="info-row" v-if="
              live.linkookReservation &&
              (reservationStartDate || reservationEndDate)
            ">
              <div class="info-label">링꾹예매</div>
              <div class="info-value">
                <p v-if="reservationStartDate">
                  시작 | {{ reservationStartDate }}
                </p>
                <p v-if="reservationEndDate">종료 | {{ reservationEndDate }}</p>
              </div>
            </div>

          </div>

          <!-- 관리자 전용 섹션 -->
          <div v-if="userStore.auth === 'admin'" class="admin-section">
            <div class="admin-section-header" @click="adminSectionOpen = !adminSectionOpen">
              <span class="admin-section-title">🔧 관리자 메뉴</span>
              <span class="admin-section-toggle">{{ adminSectionOpen ? '▲' : '▼' }}</span>
            </div>
            <div v-show="adminSectionOpen" class="admin-section-content">
              <div class="admin-info-table">
                <!-- 승인 상태 -->
                <div class="admin-info-row">
                  <div class="admin-info-label">승인 상태</div>
                  <div class="admin-info-value">
                    <span class="approval-status" :class="{
                      approved: live.approved,
                      pending: !live.approved,
                    }">
                      {{ live.approved ? "승인됨" : "승인 대기" }}
                    </span>
                    <button class="approval-toggle-btn" @click="toggleApproval" :disabled="approvalLoading">
                      {{ approvalLoading ? "처리중..." : live.approved ? "승인 취소" : "승인" }}
                    </button>
                  </div>
                </div>
                <!-- 배너 표시 -->
                <div class="admin-info-row">
                  <div class="admin-info-label">배너 표시</div>
                  <div class="admin-info-value">
                    <span class="approval-status" :class="{
                      approved: live.bannerOn,
                      pending: !live.bannerOn,
                    }">
                      {{ live.bannerOn ? "배너 표시" : "배너 숨김" }}
                    </span>
                    <span class="banner-file-status" :class="{
                      'has-file': live.banner,
                      'no-file': !live.banner,
                    }">
                      {{ live.banner ? "📁 있음" : "📁 없음" }}
                    </span>
                    <button class="approval-toggle-btn" @click="toggleBanner" :disabled="bannerLoading">
                      {{ bannerLoading ? "처리중..." : live.bannerOn ? "숨기기" : "표시" }}
                    </button>
                  </div>
                </div>
                <!-- 서비스 안내 레퍼런스 -->
                <div class="admin-info-row">
                  <div class="admin-info-label">레퍼런스</div>
                  <div class="admin-info-value">
                    <span class="approval-status" :class="{
                      approved: live.aboutReference,
                      pending: !live.aboutReference,
                    }">
                      {{ live.aboutReference ? "노출 중" : "미노출" }}
                    </span>
                    <span class="admin-sub-text">/about</span>
                    <button class="approval-toggle-btn" @click="toggleAboutReference" :disabled="referenceLoading">
                      {{ referenceLoading ? "처리중..." : live.aboutReference ? "해제" : "선택" }}
                    </button>
                  </div>
                </div>
                <!-- 주최자 정보 -->
                <div class="admin-info-row" v-if="live.organizer">
                  <div class="admin-info-label">주최자</div>
                  <div class="admin-info-value">
                    <span v-if="organizerNickname">{{ organizerNickname }}</span>
                    <span class="admin-sub-text">({{ live.organizer }})</span>
                  </div>
                </div>
                <!-- 작성자 정보 -->
                <div class="admin-info-row" v-if="live.createdBy || live.reg_id">
                  <div class="admin-info-label">작성자</div>
                  <div class="admin-info-value">
                    <span>{{ live.createdByNickname || live.reg_nickname || "알 수 없음" }}</span>
                    <span class="admin-sub-text">({{ live.createdBy || live.reg_id }})</span>
                    <span v-if="live.createdAt" class="admin-date-text">{{ formatDateTime(live.createdAt) }}</span>
                  </div>
                </div>
                <!-- 최종 수정자 정보 -->
                <div class="admin-info-row" v-if="live.lastModifiedBy">
                  <div class="admin-info-label">최종 수정</div>
                  <div class="admin-info-value">
                    <span>{{ live.lastModifiedByNickname || "알 수 없음" }}</span>
                    <span class="admin-sub-text">({{ live.lastModifiedBy }})</span>
                    <span v-if="live.lastModifiedAt" class="admin-date-text">{{ formatDateTime(live.lastModifiedAt)
                      }}</span>
                  </div>
                </div>
                <!-- 찜 정보 -->
                <div class="admin-info-row">
                  <div class="admin-info-label">찜 정보</div>
                  <div class="admin-info-value">
                    <span class="like-count-badge">❤️ {{ likeCount }}명</span>
                    <button v-if="likeCount > 0 && !likedUsersLoaded" class="show-liked-btn" @click="fetchLikedUsers"
                      :disabled="likedUsersLoading">
                      {{ likedUsersLoading ? "로딩중..." : "목록 보기" }}
                    </button>
                  </div>
                </div>
                <!-- 찜한 사용자 목록 -->
                <div v-if="likedUsersLoaded && likedUsers.length > 0" class="admin-info-row liked-users-row">
                  <div class="admin-info-label">찜한 사람</div>
                  <div class="admin-info-value liked-users-list">
                    <div v-for="user in likedUsers" :key="user.email" class="liked-user-item">
                      <span class="liked-user-avatar-placeholder">👤</span>
                      <span class="liked-user-name">{{ user.nickname }}</span>
                      <span class="liked-user-email">({{ user.email }})</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <!-- <div class="detail-like-row">
        <div class="detail-like-section"> -->
        <!-- <button
            class="detail-like-btn"
            :disabled="!userStore.isLogin"
            @click="toggleLike"
            aria-label="좋아요"
          >
            <span v-if="liked">❤️</span>
            <span v-else>🩶</span>
          </button> -->

        <!-- <button
            v-if="userStore.isLogin"
            class="detail-like-btn"
            style="font-size: 13px; line-height: 1"
            @click.stop="toggleLike()"
          >
            <img
              v-if="liked"
              class="like-heart"
              src="@/assets/images/heart.png"
            />
            <img
              v-else
              class="like-heart"
              src="@/assets/images/heart_lightgray.png"
            />
          </button> -->
        <!-- <span v-if="userStore.isLogin && userStore.auth === 'admin'" class="detail-like-count">{{
            likeCount
          }}</span> -->
        <!-- </div>
      </div> -->
      </div>

      <!-- 공연 후기/권한신청 추천 배너 -->
      <div v-if="userStore.isLogin && (canWriteReview || shouldSuggestLiveEditPermission)" class="review-banner"
        @click="goToPostActionWrite">
        <div class="review-banner-glow"></div>
        <div class="review-banner-content">
          <div class="review-icon-circle">
            <div class="review-icon">{{ shouldSuggestLiveEditPermission ? "🛠️" : "✍️" }}</div>
          </div>
          <div class="review-text">
            <h3 class="review-title">
              {{ shouldSuggestLiveEditPermission ? "공연정보 수정 권한 신청하기" : "공연 후기 작성하기" }}
            </h3>
            <p class="review-subtitle">
              {{
                shouldSuggestLiveEditPermission
                  ? "공연 관계자이신가요? 공연정보 수정 권한을 신청해보세요!"
                  : "이 공연에 대한 후기를 커뮤니티에 공유해보세요!"
              }}
            </p>
            <div class="review-badge">
              {{
                shouldSuggestLiveEditPermission
                  ? "🔑 권한신청 게시판으로 바로 이동"
                  : "💬 다른 관객들과 생각을 나눠보세요"
              }}
            </div>
          </div>
        </div>
      </div>

      <!-- 광고 지면 (하단) -->
      <AdSection :adBanners="adBanners" @ad-click="goToAdLink" />

      <!-- 탭 섹션 -->
      <div class="tabs-section">
        <div class="tab-navigation">
          <button :class="['tab-button', { active: activeTab === 'artists' }]" @click="activeTab = 'artists'">
            공연상세
          </button>
          <!-- <button
          :class="['tab-button', { active: activeTab === 'setlist' }]"
          @click="activeTab = 'setlist'"
        >
          셋리스트
        </button> -->
          <button v-if="live.linkookReservation" :class="['tab-button', { active: activeTab === 'booking' }]"
            @click="activeTab = 'booking'">
            판매정보
          </button>
        </div>

        <div class="tab-content">
          <!-- 아티스트 탭 -->
          <div v-if="activeTab === 'artists'" class="tab-panel">
            <!-- 공연 설명 (Toast UI Viewer) — 보관용. TinyMCE를 메인으로 사용 중이라 숨김 -->
            <div v-if="false && live.description" class="live-description-block">
              <div ref="descriptionViewerMount" class="description-viewer-host"></div>
            </div>

            <!-- 공연 설명 (TinyMCE · HTML) -->
            <div v-if="live.descriptionHtml" class="live-description-block live-description-html">
              <div class="description-html-host" v-html="live.descriptionHtml"></div>
            </div>

            <!-- 공연상세 이미지들 -->
            <div v-if="live.detailImageUrl || live.detailImage2Url || live.detailImage3Url || live.detailImage4Url"
              class="detail-image-section">
              <div v-if="live.detailImageUrl" class="detail-image-container">
                <img :src="live.detailImageUrl" alt="공연상세정보 1" class="detail-image zoomable"
                  @click="openLightbox(live.detailImageUrl)" />
              </div>

              <div v-if="live.detailImage2Url" class="detail-image-container">
                <img :src="live.detailImage2Url" alt="공연상세정보 2" class="detail-image zoomable"
                  @click="openLightbox(live.detailImage2Url)" />
              </div>

              <div v-if="live.detailImage3Url" class="detail-image-container">
                <img :src="live.detailImage3Url" alt="공연상세정보 3" class="detail-image zoomable"
                  @click="openLightbox(live.detailImage3Url)" />
              </div>

              <div v-if="live.detailImage4Url" class="detail-image-container">
                <img :src="live.detailImage4Url" alt="공연상세정보 4" class="detail-image zoomable"
                  @click="openLightbox(live.detailImage4Url)" />
              </div>
            </div>

            <div v-if="live.youtubeVideos && live.youtubeVideos.length > 0">
              <h1 class="artist-section-title">영상</h1>
              <div class="youtube-videos-grid">
                <div v-for="(video, idx) in live.youtubeVideos" :key="idx" class="youtube-video-item">
                  <div class="youtube-embed-wrapper">
                    <iframe :src="`https://www.youtube.com/embed/${video.vid}`" frameborder="0"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowfullscreen loading="lazy" class="youtube-embed"></iframe>
                  </div>
                  <div class="youtube-video-meta">
                    <p class="youtube-video-title">{{ video.title }}</p>
                    <p class="youtube-video-channel">{{ video.channel }}</p>
                  </div>
                </div>
              </div>
            </div>
            <div v-if="lineupArtists.length > 0">
              <h1 class="artist-section-title">라인업</h1>
              <div class="lineup-grid">
                <ArtistCard v-for="(artistName, index) in lineupArtists" :key="`lineup-${index}`" :artist="artistName"
                  :is-liked="isArtistLiked(artistName.id)" :show-like-button="true"
                  @like-toggle="handleArtistLikeToggle" />
              </div>
            </div>
            <div v-if="crewArtists.length > 0">
              <h1 class="artist-section-title">함께한 사람들</h1>
              <div class="lineup-grid">
                <ArtistCard v-for="(artistName, index) in crewArtists" :key="`crew-${index}`" :artist="artistName"
                  :role-badge="artistName.role || ''" :is-liked="isArtistLiked(artistName.id)" :show-like-button="true"
                  @like-toggle="handleArtistLikeToggle" />
              </div>
            </div>
          </div>

          <!-- 셋리스트 탭 -->
          <!-- <div v-if="activeTab === 'setlist'" class="tab-panel">
          <div v-if="live.setlist && live.setlist.length > 0" class="setlist-content">
            <div v-for="(song, index) in live.setlist" :key="index" class="setlist-item">
              <span class="song-number">{{ index + 1 }}.</span>
              <span class="song-title">{{ song }}</span>
            </div>
          </div>
          <div v-else class="empty-content">셋리스트 정보가 없습니다.</div>
        </div> -->

          <!-- 판매 정보 탭 -->
          <div v-if="activeTab === 'booking' && live.linkookReservation" class="tab-panel">
            <div class="booking-content">
              <div v-if="live.linkookPrice || live.advancePrice || live.doorPrice" class="booking-item">
                <h3 class="booking-label">판매 가격</h3>
                <p v-if="live.linkookPrice" class="booking-value">
                  링꾹판매 | {{ live.linkookPrice }}
                  <span v-if="isTicketSoldOut" class="soldout-badge">매진</span>
                  <span v-else-if="!isAdmin && !isReservationOpened" class="waiting-badge">
                    판매대기<span v-if="reservationCountdownText"> · {{ reservationCountdownText }}</span>
                  </span>
                </p>
                <p v-if="live.advancePrice" class="booking-value">
                  사전예매 | {{ live.advancePrice }}
                </p>
                <p v-if="live.doorPrice" class="booking-value">
                  현장구매 | {{ live.doorPrice }}
                </p>
              </div>

              <div v-if="live.date" class="booking-item">
                <h3 class="booking-label">공연 일시</h3>
                <p v-if="live.entryTime" class="booking-value">
                  입장 | {{ live.entryTime }}
                </p>
                <p class="booking-value">공연 | {{ live.date }}</p>
              </div>

              <div v-if="live.place" class="booking-item">
                <h3 class="booking-label">공연 장소</h3>
                <div class="booking-value place-info">
                  <span>{{ live.place }}</span>
                  <a v-if="venueMapLink" @click="goToVenueMap" class="map-link" href="#">
                    지도보기
                  </a>
                </div>
              </div>

              <div v-if="live.organizerName || live.organizerContact" class="booking-item">
                <h3 class="booking-label">판매자 정보</h3>
                <p v-if="live.organizerName" class="booking-value">판매자명 | {{ live.organizerName }}</p>
                <p v-if="live.organizerContact" class="booking-value">연락처 | {{ live.organizerContact }}</p>
              </div>

              <div v-if="
                !live.reservationLink &&
                !live.price &&
                !live.advancePrice &&
                !live.doorPrice &&
                !live.linkookPrice
              " class="empty-content">
                판매 정보가 없습니다.
              </div>
            </div>
          </div>
        </div>
      </div>

    </div>
    <div v-else class="loading">
      <div class="spinner"></div>
      <p>공연 정보를 불러오는 중...</p>
    </div>

    <!-- 수정 팝업 -->
    <linkook-live-form :is-visible="showUpdatePopup" mode="update" :live-data="live" @close-popup="closeUpdatePopup"
      @success="handleUpdateSuccess"></linkook-live-form>

    <!-- 권한관리 모달 -->
    <member-manage-modal :is-visible="showMemberManageModal" :live-id="live?.id || $route.params.id"
      :initial-members="liveMembers" @close="closeMemberManage" @updated="handleMembersUpdated"></member-manage-modal>

    <!-- 공유 방법 선택 모달 -->
    <share-options-modal :visible="showShareModal" @close="showShareModal = false"
      @select="handleShareMethod"></share-options-modal>

    <!-- 이미지 라이트박스 (포스터·상세 이미지 확대보기) -->
    <transition name="lightbox-fade">
      <div v-if="lightboxUrl" class="lightbox-overlay" @click.self="closeLightbox">
        <button class="lightbox-close" @click="closeLightbox" aria-label="닫기">✕</button>
        <img :src="lightboxUrl" class="lightbox-img" alt="이미지 전체보기" @click.stop />
      </div>
    </transition>
  </div>
</template>

<script>
import {
  getFirestore,
  doc,
  getDoc,
  collection,
  getDocs,
  query,
  where,
} from "firebase/firestore";
import { firestore, storage } from "/firebaseConfig";
import { ref } from "firebase/storage";
import { getDownloadURL } from "@/utils/storageWithNavigation";
import { useUserStore } from "@/store/user";
import apiClient from "@/utils/api";
import LinkookLiveForm from "@/components/modal/form/LiveForm.vue";
import MemberManageModal from "@/components/modal/MemberManageModal.vue";
import ArtistCard from "@/components/ArtistCard.vue";
import AdSection from "@/components/AdSection.vue";
import scrollManager from "@/utils/scrollManager";
import {
  deleteLive as deleteLiveApi,
  toggleLiveBanner as toggleLiveBannerApi,
  toggleLiveApproval as toggleLiveApprovalApi,
  toggleLiveAboutReference as toggleLiveAboutReferenceApi,
} from "@/utils/write/live";
import { toggleLiveLike as toggleLiveLikeApi } from "@/utils/write/user";
import { createNotification } from "@/utils/write/alarm";
import {
  trackLiveDetailView,
  trackTicketClick,
  trackLiveLinkClick,
} from "@/utils/statistics";
import { toggleArtistLike } from "@/utils/write/user";
import { getUserLikes } from "@/utils/read/user";
import {
  getTicketReservationsByLiveIdAndRegId,
  getTotalReservedQuantityForLive,
} from "@/utils/read/ticket";
import { incrementBannerClickCount } from "@/utils/write/banner";
import { parse } from "date-fns";
import { trackActivity, ACTIVITY_TYPES, trackShare, trackTicketClick as trackTicketClickActivity } from "@/utils/activityTracker";
import { setPageMeta, shareViaKakao, shareViaTwitter } from "@/utils/pageMeta";
import ShareOptionsModal from "@/components/modal/ShareOptionsModal.vue";
import ToastuiViewer from "@toast-ui/editor/dist/toastui-editor-viewer";
import "@toast-ui/editor/dist/toastui-editor-viewer.css";
import { prepareToastViewerMarkdown } from "@/utils/write/toastui";

export default {
  components: {
    LinkookLiveForm,
    MemberManageModal,
    ArtistCard,
    AdSection,
    ShareOptionsModal,
  },
  name: "LinkookLiveDetail",
  data() {
    return {
      live: null,
      userStore: useUserStore(),
      liked: false,
      likeCount: 0,
      showUpdatePopup: false,
      showMemberManageModal: false,
      liveMembers: [],
      approvalLoading: false,
      deleteLoading: false,
      bannerLoading: false,
      referenceLoading: false,
      adminSectionOpen: false,
      posterLoaded: false,
      venueMapLink: null,
      userArtistLikes: [], // 사용자가 찜한 아티스트 목록
      artists: [], // 전체 아티스트 정보
      activeTab: "artists", // 현재 활성화된 탭
      adBanners: [], // 광고 배너 목록
      organizerNickname: null, // 주최자 닉네임
      userReservation: null, // 사용자의 예매 정보
      totalReservedTickets: 0, // 총 예매된 티켓 수
      isTicketSoldOut: false, // 티켓 매진 여부
      reservationStartDate: null, // 예매 시작일 (문자열)
      reservationEndDate: null, // 예매 종료일 (문자열)
      reservationStartDateObj: null, // 예매 시작일 (Date 객체)
      reservationEndDateObj: null, // 예매 종료일 (Date 객체)
      // 찜한 사용자 목록 (관리자용)
      likedUsers: [],
      likedUsersLoading: false,
      likedUsersLoaded: false,
      nowTs: Date.now(),
      nowIntervalId: null,
      showShareModal: false,
      showManageDropdown: false,
      lightboxUrl: null,
    };
  },
  mounted() {
    this._onLightboxKeydown = (e) => {
      if (e.key === "Escape" && this.lightboxUrl) this.closeLightbox();
    };
    window.addEventListener("keydown", this._onLightboxKeydown);
  },
  computed: {
    isAdmin() {
      return this.userStore.auth === 'admin';
    },
    hasManagePermission() {
      // 관리 권한이 있는지 확인 (생성자, 등록자, 주최자)
      if (!this.live || !this.userStore.isLogin) return false;
      const userEmail = this.userStore.email;

      // organizer가 배열인 경우와 문자열인 경우 모두 처리
      const isOrganizer = Array.isArray(this.live.organizer)
        ? this.live.organizer.includes(userEmail)
        : this.live.organizer === userEmail;

      return (
        this.live.createdBy === userEmail ||
        this.live.reg_id === userEmail ||
        isOrganizer
      );
    },
    isReservationClosed() {
      // 예매 종료 여부 확인
      if (!this.live?.linkookReservation || !this.reservationEndDateObj) {
        return false;
      }
      return this.nowTs > this.reservationEndDateObj.getTime();
    },
    isReservationOpened() {
      // 예매 시작 여부 확인
      if (!this.live?.linkookReservation || !this.reservationStartDateObj) {
        return false; // 시작일이 없으면 기본적으로 시작된 것으로 간주
      }
      return this.nowTs >= this.reservationStartDateObj.getTime();
    },
    reservationCountdownText() {
      if (!this.live?.linkookReservation) return "";
      if (!this.reservationStartDateObj) return "";
      const diff = this.reservationStartDateObj.getTime() - this.nowTs;
      if (diff <= 0) return "";
      const totalSeconds = Math.floor(diff / 1000);
      const hh = String(Math.floor(totalSeconds / 3600)).padStart(2, "0");
      const mm = String(Math.floor((totalSeconds % 3600) / 60)).padStart(2, "0");
      const ss = String(totalSeconds % 60).padStart(2, "0");
      return `${hh}:${mm}:${ss}`;
    },
    lineupArtists() {
      const lineup = this.extractLineupNames();
      if (lineup.length === 0) return [];

      return lineup.map((artistName) => {
        // artists 배열에서 해당 아티스트 정보 찾기
        const artistInfo = this.artists.find(
          (artist) => artist.name === artistName
        );

        return {
          id: artistInfo?.id || null,
          slug: artistInfo?.slug ?? null,
          name: artistName,
          detailLink: artistInfo?.detailLink || null,
          profileImage: artistInfo?.profileImage || null,
          type: artistInfo?.type || null,
        };
      });
    },
    crewArtists() {
      const crewMembers = this.extractCrewMembers();
      if (crewMembers.length === 0) return [];

      return crewMembers.map((member) => {
        const artistInfo = this.artists.find((artist) => artist.name === member.name);

        return {
          id: artistInfo?.id || null,
          slug: artistInfo?.slug ?? null,
          name: member.name,
          role: member.role || "",
          detailLink: artistInfo?.detailLink || null,
          profileImage: artistInfo?.profileImage || null,
          type: artistInfo?.type || null,
        };
      });
    },
    lineupDisplayText() {
      if (!this.live?.lineup) return "";
      if (Array.isArray(this.live.lineup)) {
        return this.live.lineup
          .map((item) => (typeof item === "string" ? item : item?.name))
          .filter(Boolean)
          .join(", ");
      }
      return String(this.live.lineup || "").trim();
    },
    crewDisplayText() {
      // 신규 저장 포맷(검색용 문자열) 우선
      if (typeof this.live?.crews === "string" && this.live.crews.trim()) {
        return this.live.crews.trim();
      }
      // 객체 배열/문자열 배열 모두 호환
      if (Array.isArray(this.live?.crew)) {
        const names = this.live.crew
          .map((item) => (typeof item === "object" && item !== null ? item.name : item))
          .filter(Boolean)
          .map((name) => String(name).trim())
          .filter(Boolean);
        return names.join(", ");
      }
      return "";
    },
    posterBackgroundStyle() {
      return this.live?.img ? `background-image: url(${this.live.img})` : "";
    },
    eventDayDiff() {
      if (!this.live?.date) return null;
      const eventDate = parse(this.live.date.substring(0, 10), "yyyy.MM.dd", new Date());
      if (isNaN(eventDate.getTime())) return null;

      const now = new Date();
      const nowMidnight = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      const eventMidnight = new Date(
        eventDate.getFullYear(),
        eventDate.getMonth(),
        eventDate.getDate()
      );
      return Math.floor((nowMidnight - eventMidnight) / (1000 * 60 * 60 * 24));
    },
    // 공연 후기 작성 가능 여부 (공연 당일 ~ 종료 후 7일)
    canWriteReview() {
      if (!this.live?.date) return false;
      const dayDiff = this.eventDayDiff;
      if (dayDiff === null) return false;
      return dayDiff >= 0 && dayDiff <= 7;
    },
    // 공연 종료 후 일주일 초과 시 공연정보 수정 권한 신청 권유
    shouldSuggestLiveEditPermission() {
      const dayDiff = this.eventDayDiff;
      return dayDiff !== null && dayDiff > 7;
    },
  },
  async created() {
    const slug = this.$route.params.slug;

    // slug 필드로 먼저 조회, 없으면 문서 ID로 fallback (기존 데이터 호환)
    let liveDocSnap = null;
    let id = slug;

    // slug는 Number로 저장되므로 숫자 변환 시도, 실패하면 문자열 그대로 사용
    const slugValue = Number(slug);
    const slugQuery = query(
      collection(firestore, "lives"),
      where("slug", "==", isNaN(slugValue) ? slug : slugValue)
    );
    const slugResult = await getDocs(slugQuery);

    if (!slugResult.empty) {
      liveDocSnap = slugResult.docs[0];
      id = liveDocSnap.id;
    } else {
      // slug와 일치하는 문서 없음 → 문서 ID로 시도 (기존 URL 호환)
      try {
        const docRef = doc(firestore, "lives", slug);
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
          liveDocSnap = docSnap;
        }
      } catch {
        // 유효하지 않은 ID인 경우 무시
      }

      // 여전히 못 찾은 경우 → 임시저장 공연일 수 있으므로 백엔드 API로 재시도
      // (Firestore 보안 규칙이 draft 쿼리를 차단할 때 Admin SDK 우회)
      if (!liveDocSnap) {
        try {
          const resp = await apiClient.get(`/api/live/slug/${encodeURIComponent(slug)}`);
          if (resp.data?.success && resp.data.data) {
            const apiData = resp.data.data;
            id = apiData.id;
            // Firestore DocumentSnapshot 형태로 감싸서 이후 로직과 호환
            liveDocSnap = {
              exists: () => true,
              id,
              data: () => {
                // eslint-disable-next-line no-unused-vars
                const { id: _id, ...rest } = apiData;
                return rest;
              },
            };
          }
        } catch {
          // API도 실패(403/404) → live를 찾지 못한 것으로 처리
        }
      }
    }

    const docSnap = liveDocSnap;
    if (docSnap && docSnap.exists()) {
      const data = { ...docSnap.data() };

      await this.hydrateLiveDocumentStorageUrls(data);

      this.live = { ...data, id };
      this.posterLoaded = false;
      this.updatePageMeta();

      // 예매/좋아요 등 상단·예매 버튼에 필요한 데이터만 먼저 동기화
      await Promise.all([
        this.fetchLikeStatus(),
        this.checkUserReservation(),
        this.checkTicketAvailability(),
        this.fetchReservationPeriod(),
      ]);

      // 지도 링크·주최자·찜·배너·라인업 아티스트 등은 백그라운드 (첫 페인트 후 채워짐)
      void Promise.all([
        this.fetchVenueMapLink(),
        this.loadUserArtistLikes(),
        this.fetchOrganizerInfo(),
        this.fetchAdBanners(),
        this.fetchArtists(),
      ]).catch(() => {});

      this.$nextTick(() => {
        if (this.activeTab === "artists") this.mountDescriptionViewer();
      });

      // 예매 시작/종료 시각에 맞춰 UI 상태가 자동 갱신되도록 현재시간 업데이트
      this.nowIntervalId = window.setInterval(() => {
        this.nowTs = Date.now();
      }, 1000);

      // 화면 접속 통계 추적
      trackLiveDetailView(id);
    }
  },
  beforeUnmount() {
    if (this.nowIntervalId) {
      clearInterval(this.nowIntervalId);
      this.nowIntervalId = null;
    }
    this.destroyDescriptionViewer();
    if (this._onLightboxKeydown) {
      window.removeEventListener("keydown", this._onLightboxKeydown);
    }
  },
  watch: {
    activeTab(newTab) {
      if (newTab === "artists") {
        this.$nextTick(() => this.mountDescriptionViewer());
      } else {
        this.destroyDescriptionViewer();
      }
    },
  },
  methods: {
    isRepurchasableReservation(reservation) {
      const status = reservation?.status;
      // 취소/환불 관련 상태는 "이미 예매함"으로 보지 않고 재예매 가능으로 취급
      return (
        ["cancelled", "refund_requested", "refunded", "refund_completed"].includes(status) ||
        Boolean(reservation?.refundRequested)
      );
    },
    updatePageMeta() {
      if (!this.live) return;
      const desc = [this.live.place, this.live.date].filter(Boolean).join(" · ") || undefined;
      setPageMeta({
        title: this.live.title,
        description: desc,
        image: this.live.img,
      });
    },
    openLightbox(url) {
      if (!url) return;
      this.lightboxUrl = url;
    },
    closeLightbox() {
      this.lightboxUrl = null;
    },
    mountDescriptionViewer() {
      const el = this.$refs.descriptionViewerMount;
      const markdown = this.live?.description || "";
      if (!el) return;
      this.destroyDescriptionViewer();
      if (!markdown.trim()) return;
      try {
        this._descriptionViewer = new ToastuiViewer({
          el,
          initialValue: prepareToastViewerMarkdown(markdown),
          usageStatistics: false,
          extendedAutolinks: true,
          linkAttributes: { target: "_blank", rel: "noopener noreferrer" },
        });
      } catch (e) {
        console.error("ToastUI Viewer mount 실패:", e);
      }
    },
    destroyDescriptionViewer() {
      if (!this._descriptionViewer) return;
      try {
        this._descriptionViewer.destroy();
      } catch (_) {
        /* noop */
      }
      this._descriptionViewer = null;
    },
    async handleShareMethod(method) {
      this.showShareModal = false;
      const url = window.location.href;
      const title = this.live?.title || '공연';

      if (method === 'kakao') {
        await shareViaKakao();
        trackShare("live", this.live?.id, "kakao");
        return;
      }
      if (method === 'twitter') {
        shareViaTwitter();
        trackShare("live", this.live?.id, "twitter");
        return;
      }
      if (method === 'copy') {
        trackShare("live", this.live?.id, "copy");
        this.copyToClipboard(url);
        return;
      }
      if (method === 'native' && navigator.share) {
        try {
          await navigator.share({
            title,
            text: `${title} - 링꾹라이브`,
            url,
          });
          trackShare("live", this.live?.id, "native");
        } catch (error) {
          if (error.name !== 'AbortError') {
            trackShare("live", this.live?.id, "copy_fallback");
            this.copyToClipboard(url);
          }
        }
      }
    },
    copyToClipboard(text) {
      navigator.clipboard.writeText(text).then(() => {
        this.$alert('링크가 복사되었습니다.');
      }).catch(() => {
        // fallback
        const textarea = document.createElement('textarea');
        textarea.value = text;
        document.body.appendChild(textarea);
        textarea.select();
        document.execCommand('copy');
        document.body.removeChild(textarea);
        this.$alert('링크가 복사되었습니다.');
      });
    },
    onPosterLoad() {
      this.posterLoaded = true;
    },
    goToLink() {
      if (this.live && this.live.link) {
        // 링크이동 클릭 통계 추적
        trackLiveLinkClick(this.live.id);

        // 자세히보기 활동 기록
        trackActivity(ACTIVITY_TYPES.DETAIL_LINK_CLICK, {
          target: "live",
          targetId: this.live.id,
          metadata: { link: this.live.link },
        });

        window.open(this.live.link, "_blank");
      }
    },
    goToReservationLink() {
      if (!this.live) return;

      try {
        // 예매하기 링크 클릭 통계 추적
        trackTicketClick(this.live.id);
        trackTicketClickActivity(this.live.id);

        // 링꾹라이브 예매인 경우 (외부 링크 없어도 티켓폼으로 이동)
        if (this.live.linkookReservation) {
          // 비로그인 사용자는 로그인 모달 표시
          if (!this.userStore.isLogin) {
            this.userStore.showLoginPopup('liveRegister');
            return;
          }

          // 링꾹예매인데 아직 예매 시작 전(일반 사용자)은 대기 처리
          if (!this.isAdmin && !this.isReservationOpened && !this.userReservation) {
            this.$alert("예매대기");
            return;
          }

          // 이미 예매한 경우 예매 확인 페이지로 (매진 여부와 무관)
          if (this.userReservation) {
            this.$router.push({ name: "MyReservations" });
            return;
          }

          if (this.isReservationClosed) {
            this.$alert("예매가 종료되었습니다.");
            return;
          }

          if (!this.isTicketSoldOut) {
            this.$router.push(`/ticket/${this.live.id}`);
            return;
          }

          // 매진인데 외부 링크가 있으면 외부로, 없으면 안내
          if (this.live.reservationLink) {
            window.open(this.live.reservationLink, "_blank");
          } else {
            this.$alert("매진");
          }
          return;
        }

        // 일반 예매(외부 링크)인 경우
        if (this.live.reservationLink) {
          window.open(this.live.reservationLink, "_blank");
        } else {
          this.$alert("예매 링크가 없습니다.");
        }
      } finally {
        // 터치 이벤트 후 버튼 상태 초기화
        this.$nextTick(() => {
          const button = document.querySelector(".mobile-reserve-btn");
          if (button) {
            button.blur();
          }
        });
      }
    },
    getReservationButtonText() {
      // 링꾹예매 & 매진인 경우 (예매 확인 사용자 제외)
      if (this.live?.linkookReservation && this.isTicketSoldOut && !this.userReservation) {
        return "매진";
      }

      // 사용자가 이미 예매한 경우
      if (this.userReservation) {
        return "내 티켓 확인";
      }

      // 예매 종료된 경우 (예매하지 않은 사람만)
      if (this.isReservationClosed) {
        return "예매 종료";
      }

      // 예매 시작 전 표시 (관리자/일반 사용자)
      if (this.live?.linkookReservation && !this.isReservationOpened) {
        if (this.isAdmin) return "관리자 예매";
        return this.reservationCountdownText ? `예매대기 ${this.reservationCountdownText}` : "예매대기";
      }

      // 링꾹라이브 예매가 아닌 경우
      if (!this.live.linkookReservation) {
        return "예매하기";
      }

      // 티켓이 매진된 경우 (사용자가 이미 예매한 경우 제외)
      if (this.isTicketSoldOut) {
        return "매진";
      }

      // 예매하지 않은 경우
      return "링꾹에서 예매하기";
    },
    async checkUserReservation() {
      // 이메일(regId) 없음 또는 링꾹 예매가 아닌 경우
      if (!this.userStore.email || !this.live?.linkookReservation) {
        this.userReservation = null;
        return;
      }

      try {
        const list = await getTicketReservationsByLiveIdAndRegId(
          this.live.id,
          this.userStore.email
        );
        this.userReservation =
          list.find(
            (reservation) => !this.isRepurchasableReservation(reservation)
          ) || null;
      } catch (error) {
        console.error("예매 정보 확인 오류:", error);
        this.userReservation = null;
      }
    },
    async checkTicketAvailability() {
      // 링꾹 예매가 아닌 경우 체크하지 않음
      if (!this.live?.linkookReservation || !this.live?.linkookTicketCount) {
        this.totalReservedTickets = 0;
        this.isTicketSoldOut = false;
        return;
      }

      try {
        const limit = Number(this.live.linkookTicketCount);
        if (!Number.isFinite(limit) || limit <= 0) {
          this.totalReservedTickets = 0;
          this.isTicketSoldOut = false;
          return;
        }

        const totalTickets = await getTotalReservedQuantityForLive(this.live.id);
        this.totalReservedTickets = totalTickets;
        this.isTicketSoldOut = totalTickets >= limit;
      } catch (error) {
        console.error("티켓 가용성 확인 오류:", error);
        this.totalReservedTickets = 0;
        this.isTicketSoldOut = false;
      }
    },
    goToReservationManage() {
      if (this.live && this.live.linkookReservation) {
        // 링꾹라이브 예매 폼 관리 페이지로 이동 (해당 공연 ID 포함)
        this.$router.push(`/ticket-admin/${this.live.id}`);
      } else if (this.live && this.live.reservationLink) {
        // 외부 예매 링크 관리 페이지로 이동
        const manageUrl =
          this.live.reservationLink.replace(/\/$/, "") + "/manage";
        window.open(manageUrl, "_blank");
      }
    },
    goToReservationList() {
      if (this.live && this.live.linkookReservation) {
        // 예매 신청 목록 페이지로 이동
        this.$router.push(`/ticket-reservations/${this.live.id}`);
      }
    },
    onManageWrapperFocusout(e) {
      if (!e.currentTarget.contains(e.relatedTarget)) {
        this.showManageDropdown = false;
      }
    },

    editLive() {
      this.showUpdatePopup = true;
      scrollManager.lock("liveDetailUpdate");
    },
    async closeUpdatePopup() {
      this.showUpdatePopup = false;
      scrollManager.unlock("liveDetailUpdate");
    },
    openMemberManage() {
      // organizer를 배열로 정규화
      let organizerList = [];
      if (this.live?.organizer) {
        organizerList = Array.isArray(this.live.organizer)
          ? [...this.live.organizer]
          : [this.live.organizer];
      }
      this.liveMembers = organizerList;
      this.showMemberManageModal = true;
      scrollManager.lock("memberManage");
    },
    closeMemberManage() {
      this.showMemberManageModal = false;
      scrollManager.unlock("memberManage");
    },
    handleMembersUpdated(updatedMembers) {
      this.liveMembers = updatedMembers;
      if (this.live) {
        this.live.organizer = updatedMembers;
      }
    },
    async handleUpdateSuccess() {
      // 수정 성공 시 라이브 정보 다시 불러오기
      await this.reloadLiveData();
    },
    async fetchLikeStatus() {
      if (!this.live?.id) return;
      const db = getFirestore();
      // 라이브의 좋아요 수
      const liveRef = doc(db, "lives", this.live.id);
      const liveSnap = await getDoc(liveRef);
      this.likeCount = liveSnap.exists() ? liveSnap.data().likes || 0 : 0;
      // 유저의 좋아요 여부
      if (this.userStore.isLogin) {
        try {
          const { getUserLikes } = await import("@/utils/read/user");
          const likes = await getUserLikes(this.userStore.email);
          const liveLikes = likes.liveLikes || [];
          this.liked = liveLikes.includes(this.live.id);
        } catch (error) {
          console.warn("사용자 좋아요 정보 조회 실패:", error);
          this.liked = false;
        }
      } else {
        this.liked = false;
      }
    },
    // 찜한 사용자 목록 가져오기 (관리자용)
    async fetchLikedUsers() {
      if (!this.live?.id || this.likedUsersLoaded) return;

      this.likedUsersLoading = true;
      try {
        const { getUsersByLiveLike } = await import("@/utils/read/user");
        const users = await getUsersByLiveLike(this.live.id);

        this.likedUsers = users.map((user) => ({
          email: user.email,
          nickname: user.nickname || "닉네임 없음",
        }));
        this.likedUsersLoaded = true;
      } catch (error) {
        console.error("찜한 사용자 목록 가져오기 실패:", error);
      } finally {
        this.likedUsersLoading = false;
      }
    },
    async deleteLive() {
      // 권한 확인: 관리자 또는 작성자 본인만 삭제 가능
      const isCreator = this.live &&
        (this.live.reg_id === this.userStore.email ||
          this.live.createdBy === this.userStore.email);
      const isAdmin = this.userStore.auth === "admin";

      if (!isAdmin && !isCreator) {
        this.$alert("삭제 권한이 없습니다.");
        return;
      }

      // 삭제 확인
      const confirmed = await this.$confirm("정말로 이 공연을 삭제하시겠습니까?\n이 작업은 되돌릴 수 없습니다.");
      if (!confirmed) {
        return;
      }

      this.deleteLoading = true;
      try {
        // 공연 문서 삭제
        await deleteLiveApi(this.live.id);

        // 성공 메시지 표시
        this.$alert("공연이 성공적으로 삭제되었습니다.");

        // 목록 페이지로 이동
        this.$router.push("/");
      } catch (error) {
        console.error("공연 삭제 실패:", error);
        this.$alert("공연 삭제에 실패했습니다. 다시 시도해주세요.");
      } finally {
        this.deleteLoading = false;
      }
    },
    async toggleBanner() {
      if (this.userStore.auth !== "admin") return;

      this.bannerLoading = true;
      try {
        const currentBannerOn = this.live.bannerOn || false;
        const newBannerOn = await toggleLiveBannerApi(this.live.id, currentBannerOn);

        // 로컬 상태도 업데이트
        this.live.bannerOn = newBannerOn;

        // 성공 메시지 표시 (선택사항)
        // console.log(
        //   `배너가 ${this.live.bannerOn ? "표시" : "숨김"}되었습니다.`
        // );
      } catch (error) {
        console.error("배너 상태 변경 실패:", error);
        // 에러 메시지 표시 (선택사항)
      } finally {
        this.bannerLoading = false;
      }
    },
    async toggleApproval() {
      if (this.userStore.auth !== "admin") return;

      this.approvalLoading = true;
      try {
        const currentApprovalStatus = this.live.approved || false;
        const newApprovedStatus = await toggleLiveApprovalApi(this.live.id, currentApprovalStatus);

        // 로컬 상태도 업데이트
        this.live.approved = newApprovedStatus;

        // 승인 상태 변경 시 등록자에게 알림 전송 (승인/승인취소 모두)
        await this.notifyLiveCreator(newApprovedStatus);

        // 성공 메시지 표시 (선택사항)
        // console.log(
        //   `공연이 ${this.live.approved ? "승인" : "승인 취소"}되었습니다.`
        // );
      } catch (error) {
        console.error("승인 상태 변경 실패:", error);
        // 에러 메시지 표시 (선택사항)
      } finally {
        this.approvalLoading = false;
      }
    },

    async toggleAboutReference() {
      if (this.userStore.auth !== "admin") return;

      this.referenceLoading = true;
      try {
        const current = !!this.live.aboutReference;
        const next = await toggleLiveAboutReferenceApi(this.live.id, current);
        this.live.aboutReference = next;
      } catch (error) {
        console.error("안내 레퍼런스 설정 실패:", error);
        this.$alert("저장에 실패했습니다. 권한 또는 네트워크를 확인해 주세요.");
      } finally {
        this.referenceLoading = false;
      }
    },

    async notifyLiveCreator(approved) {
      try {
        // 등록자 이메일 확인 (reg_id 또는 createdBy 우선)
        const creatorEmail = this.live.reg_id || this.live.createdBy;

        if (!creatorEmail) {
          console.warn("등록자 정보가 없어 알림을 전송하지 않습니다.");
          return;
        }

        const title = approved ? "공연이 승인되었습니다" : "공연 승인이 취소되었습니다";
        const message = approved
          ? `등록하신 '${this.live.title}' 공연이 승인되었습니다.`
          : `등록하신 '${this.live.title}' 공연의 승인이 취소되었습니다.`;

        await createNotification({
          userId: creatorEmail,
          type: "live",
          title: title,
          message: message,
          liveId: this.live.id,
          read: false,
        });

      } catch (error) {
        console.error("등록자 알림 생성 오류:", error);
      }
    },

    async notifyFavoriteArtists() {
      try {
        if (!this.live.lineup) {
          console.warn("라인업이 없어서 종료");
          return;
        }

        // 라인업을 배열로 변환
        const lineup = Array.isArray(this.live.lineup)
          ? this.live.lineup
          : this.live.lineup.split(",").map((name) => name.trim());

        // 각 아티스트의 찜한 사용자들에게 알림 생성
        for (const artistName of lineup) {
          // 아티스트 정보 가져오기 (name 필드 기준으로 조회)
          const artistQuery = query(
            collection(firestore, "artists"),
            where("name", "==", artistName)
          );
          const artistSnapshot = await getDocs(artistQuery);

          if (!artistSnapshot.empty) {
            const artistData = artistSnapshot.docs[0].data();
            const likedUsers = artistData.likedUsers || [];

            // 찜한 사용자들에게 알림 생성
            for (const userEmail of likedUsers) {
              try {
                await createNotification({
                  userId: userEmail,
                  type: "live",
                  title: "새로운 공연이 등록되었습니다",
                  message: `${artistName}이(가) 출연하는 '${this.live.title}' 공연이 등록되었습니다.`,
                  liveId: this.live.id,
                  read: false,
                });
              } catch (alarmError) {
                console.error(
                  `${userEmail}에게 알림 생성 중 오류:`,
                  alarmError
                );
              }
            }
          } else {
            console.warn(`${artistName} 아티스트 문서가 존재하지 않음`);
          }
        }

      } catch (error) {
        console.error("아티스트 알림 생성 오류:", error);
        console.error("오류 상세:", error.message);
        console.error("오류 스택:", error.stack);
      }
    },
    typeIconUrl(type) {
      try {
        return require(`@/assets/images/${type}.png`);
      } catch {
        return require("@/assets/images/mix.png");
      }
    },

    formatDateTime(date) {
      if (!date) return "";

      try {
        let dateObj;
        if (date.toDate && typeof date.toDate === "function") {
          // Firestore Timestamp
          dateObj = date.toDate();
        } else if (date instanceof Date) {
          dateObj = date;
        } else {
          dateObj = new Date(date);
        }

        if (isNaN(dateObj.getTime())) return "";

        return dateObj.toLocaleString("ko-KR", {
          year: "numeric",
          month: "2-digit",
          day: "2-digit",
          hour: "2-digit",
          minute: "2-digit",
        });
      } catch (error) {
        console.error("날짜 포맷팅 오류:", error);
        return "";
      }
    },

    /**
     * lives 문서의 Storage 파일명을 다운로드 URL로 병렬 변환 (data 객체 직접 변형).
     * 기존 순차 await 대비 첫 페인트까지 대기 시간을 줄인다.
     */
    async hydrateLiveDocumentStorageUrls(data) {
      const tasks = [];

      if (data.img && typeof data.img === "string" && !data.img.startsWith("http")) {
        tasks.push(
          getDownloadURL(ref(storage, `poster/${data.img}`))
            .then((url) => {
              data.img = url;
            })
            .catch(() => {})
        );
      }

      const pushDetail = (fileField, urlField) => {
        const fname = data[fileField];
        if (fname && typeof fname === "string" && !fname.startsWith("http")) {
          tasks.push(
            getDownloadURL(ref(storage, `live-detail/${fname}`))
              .then((url) => {
                data[urlField] = url;
              })
              .catch(() => {})
          );
        }
      };

      pushDetail("detail_img", "detailImageUrl");
      pushDetail("detail_img2", "detailImage2Url");
      pushDetail("detail_img3", "detailImage3Url");
      pushDetail("detail_img4", "detailImage4Url");

      if (tasks.length === 0) return;
      await Promise.all(tasks);
    },

    async fetchVenueMapLink() {
      if (!this.live?.place) return;

      try {
        const db = getFirestore();
        const placeRef = doc(db, "places", this.live.place);
        const placeSnap = await getDoc(placeRef);

        if (placeSnap.exists()) {
          const placeData = placeSnap.data();
          this.venueMapLink = placeData.mapLink || null;
        }
      } catch (error) {
        console.warn("장소 지도 링크를 가져올 수 없습니다:", error);
      }
    },

    goToVenueMap() {
      if (this.venueMapLink) {
        // 지도보기 활동 기록
        trackActivity(ACTIVITY_TYPES.MAP_VIEW_CLICK, {
          target: "live",
          targetId: this.live?.id,
          metadata: { place: this.live?.place, mapLink: this.venueMapLink },
        });

        window.open(this.venueMapLink, "_blank");
      }
    },

    async fetchOrganizerInfo() {
      if (!this.live?.organizer) {
        this.organizerNickname = null;
        return;
      }

      const organizerEmail = String(this.live.organizer).trim().toLowerCase();
      if (
        this.userStore.isLogin &&
        this.userStore.email &&
        organizerEmail === String(this.userStore.email).trim().toLowerCase()
      ) {
        this.organizerNickname = this.userStore.nickname || null;
        return;
      }

      try {
        const { getUserInfo } = await import("@/utils/read/user");
        const userData = await getUserInfo(this.live.organizer);
        this.organizerNickname = userData?.nickname || null;
      } catch (error) {
        console.error("주최자 정보를 가져올 수 없습니다:", error);
        this.organizerNickname = null;
      }
    },
    async fetchReservationPeriod() {
      // 링꾹 예매가 아니면 가져오지 않음
      if (!this.live?.linkookReservation || !this.live?.id) {
        this.reservationStartDate = null;
        this.reservationEndDate = null;
        this.reservationStartDateObj = null;
        this.reservationEndDateObj = null;
        return;
      }

      // lives 문서에 저장된 문자열(YYYY.MM.DD HH:mm) 사용
      const parseReservationStr = (s) => {
        if (typeof s !== "string") return null;
        const m = /^(\d{4})\.(\d{2})\.(\d{2}) (\d{2}):(\d{2})$/.exec(s.trim());
        if (!m) return null;
        return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]), Number(m[4]), Number(m[5]));
      };

      this.reservationStartDate = this.live?.reservationStartDate || null;
      this.reservationEndDate = this.live?.reservationEndDate || null;
      this.reservationStartDateObj = parseReservationStr(this.reservationStartDate);
      this.reservationEndDateObj = parseReservationStr(this.reservationEndDate);
    },
    formatReservationDate(date) {
      if (!date) return "";

      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const day = String(date.getDate()).padStart(2, "0");
      const hours = String(date.getHours()).padStart(2, "0");
      const minutes = String(date.getMinutes()).padStart(2, "0");

      return `${year}.${month}.${day} ${hours}:${minutes}`;
    },
    // 찜 버튼 클릭 핸들러
    handleLikeClick() {
      if (!this.userStore.isLogin) {
        this.userStore.showLoginPopup('liveLike');
        return;
      }
      this.toggleLike();
    },
    async toggleLike() {
      const currentLikedState = this.liked || false;
      this.liked = !this.liked;
      try {
        await toggleLiveLikeApi(this.live.id, this.userStore.email, currentLikedState);
        // await this.fetchLikeStatus();
      } catch (e) {
        this.liked = !this.liked;
        this.$alert("좋아요 처리 중 오류가 발생했습니다.");
      }
    },

    async fetchArtists() {
      try {
        const lineupNames = this.extractLineupNames();
        const crewNames = this.extractCrewMembers().map((member) => member.name);
        const targetNames = [...new Set([...lineupNames, ...crewNames])];

        // 라인업/함께한사람들이 모두 없으면 아티스트 정보를 가져오지 않음
        if (targetNames.length === 0) {
          this.artists = [];
          return;
        }

        // 라인업+함께한사람들에 포함된 아티스트만 조회 (name 필드 기준)
        this.artists = await Promise.all(
          targetNames.map(async (artistName) => {
            try {
              // name 필드 기준으로 아티스트 조회
              const artistQuery = query(
                collection(firestore, "artists"),
                where("name", "==", artistName)
              );
              const artistSnapshot = await getDocs(artistQuery);

              if (artistSnapshot.empty) {
                return {
                  id: null,
                  slug: null,
                  name: artistName,
                  type: null,
                  detailLink: null,
                  profileImage: null,
                };
              }

              const artistDoc = artistSnapshot.docs[0];
              const artistData = artistDoc.data();

              // 승인되지 않은 아티스트는 관리자가 아니면 제외
              if (!artistData.approved && this.userStore.auth !== "admin") {
                return {
                  id: null,
                  slug: null,
                  name: artistName,
                  type: null,
                  detailLink: null,
                  profileImage: null,
                };
              }

              let profileImageUrl = null;

              // 프로필 이미지가 있으면 다운로드 URL 생성
              // userProfileImg가 있으면 profile 폴더에서, img가 있으면 경로 확인 후 로드
              if (artistData.userProfileImg) {
                try {
                  const imageRef = ref(storage, `profile/${artistData.userProfileImg}`);
                  profileImageUrl = await getDownloadURL(imageRef);
                } catch (error) {
                  // 이미지 로드 실패시 null 유지
                  profileImageUrl = null;
                }
              } else if (artistData.img) {
                try {
                  // img에 경로가 포함된 경우 (예: profile/xxx.jpg) 그대로 사용
                  let imagePath;
                  if (artistData.img.includes("/")) {
                    imagePath = artistData.img;
                  } else {
                    imagePath = `artist/${artistData.img}`;
                  }
                  const imageRef = ref(storage, imagePath);
                  profileImageUrl = await getDownloadURL(imageRef);
                } catch (error) {
                  // 이미지 로드 실패시 null 유지
                  profileImageUrl = null;
                }
              }

              return {
                id: artistDoc.id,
                slug: artistData.slug ?? null,
                name: artistData.name,
                type: artistData.type || null,
                detailLink: artistData.detailLink || null,
                profileImage: profileImageUrl,
              };
            } catch (error) {
              console.error(`아티스트 ${artistName} 정보 로드 오류:`, error);
              return {
                id: null,
                slug: null,
                name: artistName,
                type: null,
                detailLink: null,
                profileImage: null,
              };
            }
          })
        );
      } catch (error) {
        console.error("아티스트 정보 로드 오류:", error);
        this.artists = [];
      }
    },
    extractLineupNames() {
      if (!this.live?.lineup) return [];
      const lineup = Array.isArray(this.live.lineup)
        ? this.live.lineup
        : String(this.live.lineup).split(",");
      return lineup
        .map((item) => (typeof item === "object" && item !== null ? item.name : item))
        .map((name) => String(name || "").trim())
        .filter(Boolean);
    },
    extractCrewNames() {
      return this.extractCrewMembers().map((member) => member.name);
    },
    extractCrewMembers() {
      if (Array.isArray(this.live?.crew)) {
        return this.live.crew
          .map((item) => {
            if (typeof item === "object" && item !== null) {
              return {
                name: String(item.name || "").trim(),
                role: String(item.role || "").trim(),
              };
            }
            return {
              name: String(item || "").trim(),
              role: "",
            };
          })
          .filter((member) => member.name);
      }
      if (typeof this.live?.crews === "string" && this.live.crews.trim()) {
        return this.live.crews
          .split(",")
          .map((name) => ({
            name: String(name || "").trim(),
            role: "",
          }))
          .filter((member) => member.name);
      }
      return [];
    },
    async loadUserArtistLikes() {
      if (!this.userStore.isLogin) {
        this.userArtistLikes = [];
        return;
      }

      try {
        const userLikes = await getUserLikes(this.userStore.email);
        this.userArtistLikes = userLikes.artistLikes || [];
      } catch (error) {
        console.error("사용자 찜 데이터 로드 오류:", error);
        this.userArtistLikes = [];
      }
    },

    isArtistLiked(artistId) {
      return this.userArtistLikes.includes(artistId);
    },

    async handleArtistLikeToggle(artistId) {
      if (!this.userStore.isLogin) {
        this.userStore.showLoginPopup('artistLike');
        return;
      }

      try {
        const isCurrentlyLiked = this.isArtistLiked(artistId);

        await toggleArtistLike(
          artistId,
          this.userStore.email,
          isCurrentlyLiked
        );

        // 로컬 상태 업데이트
        await this.loadUserArtistLikes();
      } catch (error) {
        console.error("아티스트 찜 처리 중 오류:", error);
        this.$alert("찜 처리 중 오류가 발생했습니다.");
      }
    },

    async reloadLiveData() {
      try {
        const id = this.live?.id || this.$route.params.slug;
        const docRef = doc(firestore, "lives", id);
        const docSnap = await getDoc(docRef);

        if (docSnap.exists()) {
          const data = { ...docSnap.data() };

          await this.hydrateLiveDocumentStorageUrls(data);

          this.live = { ...data, id };
          this.posterLoaded = false;
          this.updatePageMeta();
          this.$nextTick(() => {
            if (this.activeTab === "artists") this.mountDescriptionViewer();
          });

          await Promise.all([
            this.fetchLikeStatus(),
            this.checkUserReservation(),
            this.checkTicketAvailability(),
            this.fetchReservationPeriod(),
          ]);

          void Promise.all([
            this.fetchVenueMapLink(),
            this.fetchArtists(),
            this.loadUserArtistLikes(),
            this.fetchOrganizerInfo(),
            this.fetchAdBanners(),
          ]).catch(() => {});
        }
      } catch (error) {
        console.error("라이브 정보 새로고침 실패:", error);
      }
    },

    async fetchAdBanners() {
      try {
        // banners 컬렉션에서 active가 true이고 type이 "detail"인 데이터 가져오기
        const bannersQuery = query(
          collection(firestore, "banners"),
          where("active", "==", true),
          where("type", "==", "detail")
        );
        const bannersSnapshot = await getDocs(bannersQuery);

        const customBannerData = await Promise.all(
          bannersSnapshot.docs.map(async (doc) => {
            const data = doc.data();

            // 광고 기간 체크 (startDateTime과 endDateTime이 있는 경우)
            if (data.startDateTime || data.endDateTime) {
              const now = new Date();

              // 시작일시 체크
              if (data.startDateTime) {
                const startDate = new Date(data.startDateTime);
                if (now < startDate) {
                  // 아직 광고 시작 전
                  return null;
                }
              }

              // 종료일시 체크
              if (data.endDateTime) {
                const endDate = new Date(data.endDateTime);
                if (now > endDate) {
                  // 광고 기간 종료됨
                  return null;
                }
              }
            }

            // 배너 이미지 URL 처리
            let bannerImgUrl = data.img;
            if (!bannerImgUrl) {
              console.error("배너 이미지가 없습니다:", data.title);
              return null;
            }

            if (!bannerImgUrl.startsWith("http")) {
              try {
                const imageRef = ref(storage, `banner/${data.img}`);
                bannerImgUrl = await getDownloadURL(imageRef);
              } catch (e) {
                console.error("배너 이미지 로드 실패:", e);
                return null;
              }
            }

            return {
              id: doc.id,
              source: "custom", // 출처 표시
              title: data.title,
              img: bannerImgUrl,
              link: data.link || "",
              date: data.date || "",
              place: data.place || "",
              type: "site", // 커스텀 배너는 site 타입으로
              priority: data.priority || 0, // 우선순위
            };
          })
        );

        const allBanners = customBannerData.filter(Boolean);

        // 우선순위로 정렬 (priority가 높을수록 먼저 표시)
        allBanners.sort((a, b) => {
          const priorityA = a.priority || 0;
          const priorityB = b.priority || 0;
          return priorityB - priorityA;
        });

        this.adBanners = allBanners;
        // console.log("배너 데이터 개수:", this.adBanners.length);
      } catch (error) {
        console.error("광고 배너 가져오기 실패:", error);
        this.adBanners = [];
      }
    },

    async goToAdLink(ad) {
      // 클릭 횟수 증가
      await this.incrementAdClickCount(ad);

      // 커스텀 배너(source: "custom")이고 링크가 있는 경우 외부 링크로 이동
      if (ad.source === "custom" && ad.link) {
        window.open(ad.link, "_blank");
      } else if (ad.source === "live") {
        // 라이브 배너인 경우 해당 라이브 상세 페이지로 이동
        this.$router.push({
          name: "LiveDetail",
          params: { slug: ad.slug || ad.id },
        });
      }
    },

    async incrementAdClickCount(ad) {
      try {
        await incrementBannerClickCount(ad);
      } catch (error) {
        console.error("광고 클릭 횟수 증가 오류:", error);
        // 클릭 횟수 증가 실패해도 사용자 경험에는 영향을 주지 않음
      }
    },

    // 공연 후기 작성 페이지로 이동
    goToReviewWrite() {
      if (!this.live) return;

      // 글쓰기 페이지로 이동 (공연후기 카테고리 자동 선택)
      this.$router.push({
        path: "/board/write",
        query: {
          category: "performance",
          liveTitle: this.live.title,
        },
      });
    },
    // 공연정보 수정 권한 신청 페이지로 이동
    goToLiveEditPermissionWrite() {
      if (!this.live) return;

      this.$router.push({
        path: "/board/write",
        query: {
          category: "permission",
          liveTitle: this.live.title,
        },
      });
    },
    goToPostActionWrite() {
      if (this.shouldSuggestLiveEditPermission) {
        this.goToLiveEditPermissionWrite();
        return;
      }
      this.goToReviewWrite();
    },

    // D-Day 계산 (LinkookLiveTiles와 동일한 로직)
    calculateDaysLeft(targetDate) {
      const substrDate = targetDate.substring(0, 10);
      if (!substrDate) return "Invalid date";
      const now = new Date();
      const eventDate = parse(substrDate, "yyyy.MM.dd", new Date()); // 파싱
      if (isNaN(eventDate.getTime())) return "Invalid date";

      const nowMidnight = new Date(
        now.getFullYear(),
        now.getMonth(),
        now.getDate()
      );
      const eventMidnight = new Date(
        eventDate.getFullYear(),
        eventDate.getMonth(),
        eventDate.getDate()
      );

      const daysLeft = Math.ceil(
        (eventMidnight - nowMidnight) / (1000 * 60 * 60 * 24)
      );
      // 공연 당일까지는 D-Day로 표시 (daysLeft가 0 이상이면)
      return daysLeft === 0 ? "D-DAY" : daysLeft > 0 ? `D-${daysLeft}` : `종료`;
    },
  },
};
</script>

<style scoped>
/* 로딩 상태 */
.loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px;
  min-height: 50vh;
}

.spinner {
  width: 50px;
  height: 50px;
  border: 5px solid #f3f3f3;
  border-top: 5px solid #66bdff;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 20px;
}

.ad-image {
  border-radius: 8px;
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }

  100% {
    transform: rotate(360deg);
  }
}

.loading p {
  color: #666;
  font-size: 16px;
  font-weight: 500;
  font-family: "NanumSquareRoundR", sans-serif;
}

/* ========================================
   반응형 디자인
   - Medium: 481px ~ (기본 스타일, 최대 1024px)
   - Small: ~480px
   ======================================== */

.linkook-live-detail-wrapper {
  width: 100%;
  padding-bottom: 10px;
}

.detail-container {
  margin: auto;
  width: 90%;
  background: #fff;
  padding: 0px 16px;
  margin-bottom: 80px;
}

.draft-notice {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 14px 18px;
  margin: 12px 0;
  background: #e8f5e9;
  border: 2px dashed #66bb6a;
  border-radius: 10px;
  color: #2e7d32;
  font-size: 14px;
  line-height: 1.5;
}

.draft-notice-icon {
  flex-shrink: 0;
  width: 44px;
  height: 44px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(102, 187, 106, 0.2);
  border-radius: 12px;
  font-size: 22px;
}

.draft-notice-text {
  flex: 1;
  min-width: 0;
}

.draft-notice-label {
  display: block;
  font-weight: 700;
  font-size: 15px;
  margin-bottom: 4px;
}

.draft-notice-desc {
  margin: 0;
  font-size: 13px;
  color: #388e3c;
  line-height: 1.45;
}

/* 승인 대기 안내 (임시저장 배너와 동일 위치/스타일) */
.approval-pending-notice {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 14px 18px;
  margin: 12px 0;
  background: #fff8e1;
  border: 2px dashed #ffb74d;
  border-radius: 10px;
  color: #e65100;
  font-size: 14px;
  line-height: 1.5;
}

.approval-pending-icon {
  flex-shrink: 0;
  width: 44px;
  height: 44px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 183, 77, 0.25);
  border-radius: 12px;
  font-size: 22px;
}

.approval-pending-text {
  flex: 1;
  min-width: 0;
}

.approval-pending-label {
  display: block;
  font-weight: 700;
  font-size: 15px;
  margin-bottom: 4px;
}

.approval-pending-desc {
  margin: 0;
  font-size: 13px;
  color: #ef6c00;
  line-height: 1.45;
}

.detail-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin: 16px 0px;
}

.back-arrow-btn {
  background: transparent;
  border: none;
  font-size: 20px;
  cursor: pointer;
  padding: 6px 10px;
  color: #333;
  transition: opacity 0.2s;
}

.back-arrow-btn:hover {
  opacity: 0.7;
}

.share-btn {
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 4px 8px;
  display: flex;
  justify-content: center;
  transition: opacity 0.2s;
}

.share-btn:hover {
  opacity: 0.7;
}

.share-icon {
  width: 22px;
  height: 22px;
  object-fit: contain;
}

.manage-btn-wrapper {
  position: relative;
  flex-shrink: 0;
}

.manage-settings-btn {
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 4px 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: opacity 0.2s ease;
  border-radius: 6px;
}

.manage-settings-btn:hover {
  opacity: 0.7;
}

.settings-icon {
  width: 22px;
  height: 22px;
  object-fit: contain;
  opacity: 0.45;
}

.manage-dropdown {
  position: absolute;
  top: calc(100% + 6px);
  right: 0;
  background: #fff;
  border: 1px solid #e0e0e0;
  border-radius: 10px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.12);
  min-width: 120px;
  z-index: 100;
  overflow: hidden;
}

.manage-dropdown-item {
  display: block;
  width: 100%;
  padding: 10px 16px;
  background: transparent;
  border: none;
  text-align: left;
  font-size: 0.9rem;
  color: #333;
  cursor: pointer;
  transition: background 0.15s;
}

.manage-dropdown-item:hover {
  background: #f5f5f5;
}

.manage-dropdown-item--danger {
  color: #e53935;
}

.manage-dropdown-item--danger:hover {
  background: #fff5f5;
}

.detail-title {
  width: 100%;
  margin-bottom: 20px;
  display: flex;
  justify-content: space-between;
  gap: 12px;
}

.title-left {
  display: flex;
  align-items: baseline;
  gap: 12px;
  flex-wrap: wrap;
  flex: 1;
}

.detail-title h1 {
  font-size: 1.25rem;
  color: #333;
  margin: 0;
  word-break: keep-all;
  font-family: "Paperlogy8", "NanumSquareRoundEB", sans-serif;
  letter-spacing: -0.5px;
  line-height: 1.4;
}

/* 데스크톱/모바일 전용 표시 */
.mobile-only {
  display: flex;
}

.desktop-only {
  display: none;
}

.detail-more-link {
  font-size: 12px;
  color: #2196f3;
  cursor: pointer;
  text-decoration: none;
  white-space: nowrap;
  transition: all 0.2s;
  font-family: "NanumSquareRoundR", sans-serif;
}

.detail-more-link:hover {
  color: #2196f3;
  text-decoration: underline;
}

.twitter-icon {
  width: 16px;
  height: 16px;
  filter: brightness(0) invert(1);
}

.back-btn {
  background: #fff;
  color: #222;
  border: 1px solid #ccc;
  border-radius: 8px;
  padding: 10px 15px;
  font-size: 18px;
  cursor: pointer;
}

.edit-btn {
  font-size: 12px;
  padding: 5px 10px;
  border-radius: 5px;
  background: #2196f3;
  color: #fff;
  border: none;
  cursor: pointer;
  transition: background 0.15s;
  margin-right: 10px;
  margin-bottom: 10px;
}

.edit-btn:hover {
  background: #1976d2;
}

.reservation-manage-btn {
  font-size: 12px;
  padding: 5px 10px;
  border-radius: 5px;
  background: linear-gradient(135deg, #66bdff 0%, #ff69b4 100%);
  color: #fff;
  border: none;
  cursor: pointer;
  transition: all 0.15s;
  margin-right: 10px;
  font-family: "NanumSquareRoundR", sans-serif;
  box-shadow: 0 2px 8px rgba(102, 189, 255, 0.3);
}

.reservation-manage-btn:hover {
  background: linear-gradient(135deg, #4a9eff 0%, #e04893 100%);
  box-shadow: 0 4px 12px rgba(102, 189, 255, 0.4);
  transform: translateY(-1px);
}

.reservation-list-btn {
  font-size: 12px;
  padding: 5px 10px;
  border-radius: 5px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: #fff;
  border: none;
  cursor: pointer;
  transition: all 0.15s;
  margin-right: 10px;
  font-family: "NanumSquareRoundR", sans-serif;
  box-shadow: 0 2px 8px rgba(102, 126, 234, 0.3);
}

.reservation-list-btn:hover {
  background: linear-gradient(135deg, #5568d3 0%, #66398d 100%);
  box-shadow: 0 4px 12px rgba(102, 126, 234, 0.4);
  transform: translateY(-1px);
}

.member-manage-btn {
  font-size: 12px;
  padding: 5px 10px;
  border-radius: 5px;
  background: linear-gradient(135deg, #ff9a56 0%, #ff6a00 100%);
  color: #fff;
  border: none;
  cursor: pointer;
  transition: all 0.15s;
  margin-right: 10px;
  font-family: "NanumSquareRoundR", sans-serif;
  box-shadow: 0 2px 8px rgba(255, 154, 86, 0.3);
}

.member-manage-btn:hover {
  background: linear-gradient(135deg, #ff8a40 0%, #f55d00 100%);
  box-shadow: 0 4px 12px rgba(255, 154, 86, 0.4);
  transform: translateY(-1px);
}

.delete-btn {
  font-size: 12px;
  padding: 5px 10px;
  border-radius: 5px;
  background: #f44336;
  color: #fff;
  border: none;
  cursor: pointer;
  transition: background 0.15s;
  font-family: "NanumSquareRoundR", sans-serif;
  margin-right: 10px;
  margin-bottom: 10px;
}

.delete-btn:hover {
  background: #d32f2f;
}

.delete-btn:disabled {
  background: #ccc;
  cursor: not-allowed;
}

.detail-content {
  display: flex;
  flex-direction: column;
  gap: 18px;
  align-items: stretch;
  margin-top: 10px;
}

.detail-poster-container {
  width: 100%;
  height: 350px;
  position: relative;
  overflow: hidden;
  border-radius: 18px;
  background: #000;
}

.poster-blur-background {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-size: cover;
  background-position: center;
  filter: blur(4px);
  transform: scale(1.1);
  opacity: 0.3;
  z-index: 1;
}

.detail-poster {
  position: absolute;
  top: 39%;
  left: 50%;
  transform: translate(-50%, -50%);
  max-width: 50%;
  max-height: 70%;
  width: auto;
  height: auto;
  border-radius: 0px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
  z-index: 2;
  opacity: 0;
  cursor: zoom-in;
  transition: opacity 0.3s ease;
}

.zoomable {
  cursor: zoom-in;
}

.lightbox-overlay {
  position: fixed;
  inset: 0;
  z-index: 9999;
  background: rgba(0, 0, 0, 0.88);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  box-sizing: border-box;
}

.lightbox-img {
  max-width: 100%;
  max-height: 90vh;
  object-fit: contain;
  border-radius: 10px;
  box-shadow: 0 8px 40px rgba(0, 0, 0, 0.5);
  display: block;
  user-select: none;
}

.lightbox-close {
  position: fixed;
  top: 20px;
  right: 20px;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  border: none;
  background: rgba(255, 255, 255, 0.15);
  color: #fff;
  font-size: 18px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.2s;
  z-index: 10000;
  backdrop-filter: blur(4px);
}

.lightbox-close:hover {
  background: rgba(255, 255, 255, 0.3);
}

.lightbox-fade-enter-active,
.lightbox-fade-leave-active {
  transition: opacity 0.2s ease;
}

.lightbox-fade-enter-from,
.lightbox-fade-leave-to {
  opacity: 0;
}

.detail-poster.poster-loaded {
  opacity: 1;
}

.poster-wrapper {
  position: relative;
  display: inline-block;
  width: 100%;
  min-width: 318px;
  min-height: 450px;
}

/* 포스터 스켈레톤 UI */
.poster-skeleton {
  position: absolute;
  top: 0;
  left: 0;
  width: 318px;
  height: 450px;
  background: linear-gradient(135deg, #f0f0f0 0%, #e8e8e8 100%);
  border-radius: 12px;
  overflow: hidden;
  z-index: 11;
}

.skeleton-shimmer {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(90deg,
      transparent 0%,
      rgba(255, 255, 255, 0.5) 50%,
      transparent 100%);
  animation: shimmer 1.5s infinite;
}

@keyframes shimmer {
  0% {
    transform: translateX(-100%);
  }

  100% {
    transform: translateX(100%);
  }
}

.detail-info {
  display: flex;
  flex-direction: column;
  gap: 16px;
  min-width: 0;
  width: 100%;
  padding: 0;
  margin-top: 0px;
  margin-bottom: 24px;
}

.info-table {
  display: flex;
  flex-direction: column;
  gap: 12px;
  min-width: 0;
  width: 100%;
}

.info-row {
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  gap: 16px;
}

.info-label {
  width: 70px;
  min-width: 70px;
  color: #888;
  font-size: 12px;
  font-weight: 600;
  text-align: left;
  font-family: "NanumSquareRoundEB", sans-serif;
  letter-spacing: -0.3px;
  padding-top: 2px;
  margin-top: 1px;
}

.info-value {
  color: #333;
  font-size: 13px;
  font-weight: 400;
  text-align: left;
  word-break: keep-all;
  font-family: "Pretendard", sans-serif;
  line-height: 1.5;
  letter-spacing: -0.2px;
}

.info-value p {
  margin: 0px;
}

.type-with-icon {
  display: flex;
  align-items: center;
  gap: 8px;
}

.place-with-map {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

.map-view-link {
  color: #666;
  text-decoration: none;
  font-size: 12px;
  cursor: pointer;
  transition: color 0.2s ease;
  font-family: "NanumSquareRoundR", sans-serif;
  white-space: nowrap;
  border: none;
  padding: 0px;
}

.map-view-link:hover {
  color: #333;
  text-decoration: underline;
}

.type-icon {
  width: 22px;
  height: 22px;
  object-fit: contain;
  border: 1px solid #e0e0e0;
  border-radius: 50%;
  padding: 2px;
}

/* 티켓 가격 정보 스타일 */
.ticket-price-info {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.ticket-price.linkook-price {
  color: #333;
  font-weight: 400;
  display: flex;
  align-items: center;
  gap: 8px;
}

.soldout-badge {
  display: inline-block;
  padding: 2px 8px;
  background: #ff5252;
  color: white;
  font-size: 12px;
  font-weight: bold;
  border-radius: 6px;
  font-family: "NanumSquareRoundB", sans-serif;
  box-shadow: 0 2px 6px rgba(255, 82, 82, 0.3);
}

.waiting-badge {
  display: inline-block;
  padding: 2px 8px;
  background: rgba(102, 189, 255, 0.18);
  color: #1976d2;
  font-size: 12px;
  font-weight: bold;
  border-radius: 6px;
  font-family: "NanumSquareRoundB", sans-serif;
  border: 1px solid rgba(102, 189, 255, 0.35);
}

/* 탭 섹션 스타일 */
.tabs-section {
  margin-top: 24px;
  width: 100%;
  background: #fafbfc;
  overflow: hidden;
  margin-bottom: 70px;
  border-radius: 10px;
}

.tab-navigation {
  display: flex;
  background: #fff;
  border-bottom: 1px solid #e0e0e0;
  flex-direction: row;
}

.tab-button {
  flex: 1;
  padding: 12px 8px;
  border: none;
  background: none;
  font-size: 14px;
  font-weight: 500;
  color: #666;
  cursor: pointer;
  transition: all 0.2s ease;
  font-family: "NanumSquareRoundR", sans-serif;
  border-bottom: 3px solid transparent;
  border-radius: 0px;
}

.tab-button:hover {
  color: #333;
  background: #f5f5f5;
}

.tab-button.active {
  color: #2196f3;
  background: #f8fafe;
  border-bottom-color: #2196f3;
}

.tab-content {
  padding: 18px 8px 14px 8px;
  min-height: 200px;
  background-color: #ffffff;
}

.tab-panel {
  animation: fadeIn 0.3s ease-in-out;
}

.live-description-block {
  margin-top: 4px;
  margin-bottom: 24px;
  padding-bottom: 20px;
}

.live-description-block .description-viewer-host {
  width: 100%;
}

.live-description-block :deep(.toastui-editor-contents) {
  font-family: "Pretendard", "NanumSquareRoundR", sans-serif;
  font-size: 14px;
  color: #444;
  line-height: 1.7;
  padding: 0;
}

.live-description-block :deep(.toastui-editor-contents img) {
  max-width: 100%;
  height: auto;
  border-radius: 6px;
}

.live-description-block :deep(.toastui-editor-contents p) {
  margin: 0 0 8px 0;
}

.live-description-block :deep(.toastui-editor-contents a) {
  color: #66bdff;
  text-decoration: underline;
  word-break: break-all;
}

/* TinyMCE HTML 설명 블록 */
.live-description-html .description-html-host {
  font-family: "Pretendard", "NanumSquareRoundR", sans-serif;
  font-size: 14px;
  color: #444;
  line-height: 1.7;
  text-align: left;
  word-break: break-word;
}

.live-description-html .description-html-host :deep(p) {
  margin: 0 0 8px 0;
}

.live-description-html .description-html-host :deep(img) {
  max-width: 100%;
  height: auto;
  border-radius: 6px;
}

.live-description-html .description-html-host :deep(a) {
  color: #66bdff;
  text-decoration: underline;
  word-break: break-all;
}

.live-description-html .description-html-host :deep(table) {
  border-collapse: collapse;
  width: 100%;
  margin: 8px 0;
}

.live-description-html .description-html-host :deep(table th),
.live-description-html .description-html-host :deep(table td) {
  border: 1px solid #e0e0e0;
  padding: 6px 10px;
}

.live-description-html .description-html-host :deep(blockquote) {
  border-left: 3px solid #ddd;
  padding: 4px 12px;
  color: #666;
  margin: 8px 0;
}

.live-description-html .description-html-host :deep(iframe) {
  max-width: 100%;
  aspect-ratio: 16 / 9;
  height: auto;
  border: 0;
  border-radius: 6px;
}

.detail-image-section {
  margin-bottom: 32px;
  text-align: center;
}

.detail-image-container {
  margin-bottom: 24px;
}

.detail-image-container:last-child {
  margin-bottom: 0;
}

.detail-image {
  max-width: 100%;
  height: auto;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.fallback-image-notice {
  margin-top: 12px;
  padding: 8px 16px;
  background: #f0f8ff;
  border: 1px solid #b3d9ff;
  border-radius: 6px;
  color: #0066cc;
  font-size: 14px;
  text-align: center;
  font-style: italic;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(10px);
  }

  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.lineup-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 12px;
  max-width: 800px;
  margin: 0 auto;
}

.artist-section-title {
  max-width: 800px;
  margin: 16px auto 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  font-size: 15px;
  color: #333;
  font-family: "NanumSquareRoundB", sans-serif;
}

.artist-section-title::before,
.artist-section-title::after {
  content: "";
  flex: 1;
  height: 1px;
  background: #e5e5e5;
}

.empty-content {
  text-align: center;
  color: #999;
  font-size: 14px;
  padding: 40px 15px;
  font-family: "NanumSquareRoundR", sans-serif;
}

/* 셋리스트 스타일 */
.setlist-content {
  max-width: 600px;
  margin: 0 auto;
}

.setlist-item {
  display: flex;
  align-items: center;
  padding: 10px 12px;
  margin-bottom: 6px;
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  transition: transform 0.2s ease;
  font-size: 14px;
}

.setlist-item:hover {
  transform: translateX(4px);
}

.song-number {
  font-weight: bold;
  color: #2196f3;
  margin-right: 8px;
  min-width: 25px;
  font-family: "NanumSquareRoundB", sans-serif;
  font-size: 14px;
}

.song-title {
  flex: 1;
  font-size: 14px;
  color: #333;
  font-family: "NanumSquareRoundR", sans-serif;
}

/* 판매 정보 스타일 */
.booking-content {
  margin: 0 auto;
}

.booking-item {
  margin-bottom: 16px;
  padding: 16px;
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
}

.booking-label {
  font-size: 14px;
  font-weight: bold;
  color: #333;
  margin-top: 0px;
  margin-bottom: 8px;
  font-family: "NanumSquareRoundB", sans-serif;
}

.booking-value {
  font-size: 14px;
  color: #666;
  margin: 0;
  font-family: "NanumSquareRoundR", sans-serif;
}

.place-info {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
}

.map-link {
  color: #666;
  text-decoration: none;
  font-size: 14px;
  cursor: pointer;
  transition: color 0.2s ease;
  font-family: "NanumSquareRoundR", sans-serif;
  white-space: nowrap;
  border: 1px solid #999;
  padding: 4px 12px;
  border-radius: 12px;
}

.map-link:hover {
  color: #333;
  text-decoration: underline;
}

.booking-link-button {
  background: #2196f3;
  color: white;
  border: none;
  border-radius: 8px;
  padding: 10px 20px;
  font-size: 14px;
  font-weight: bold;
  cursor: pointer;
  transition: all 0.2s ease;
  font-family: "NanumSquareRoundR", sans-serif;
}

.booking-link-button:hover {
  background: #1976d2;
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(33, 150, 243, 0.3);
}

.detail-like-row {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 8px;
  width: 100%;
  margin: 10px 0 0 0;
  font-size: 15px;
}

.detail-like-btn {
  background: none;
  border: none;
  cursor: pointer;
  font-size: 16px;
  padding: 0 4px;
  margin-bottom: 50px;
  transition: opacity 0.15s;
}

.detail-like-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.detail-like-count {
  font-size: 16px;
  color: #888;
  font-family: "NanumSquareRoundR", sans-serif;
}

/* 하단 고정 예매 버튼 (전체 화면) */
.mobile-reserve-btn {
  position: fixed;
  bottom: 0px;
  left: 0;
  right: 0;
  width: 100%;
  max-width: 1024px;
  margin: 0 auto;
  z-index: 10;
  font-size: 18px;
  font-weight: bold;
  padding: 16px 20px calc(16px + env(safe-area-inset-bottom));
  background: #fff;
  border-top: 1px solid #e8e8e8;
  box-shadow: 0 -4px 20px rgba(0, 0, 0, 0.06);
}

/* 일반 예매 버튼 스타일 (기본) */
.reserve-btn-inner {
  display: block;
  width: 100%;
  margin: auto;
  background: #fff;
  color: #2196f3;
  border: 2px solid #2196f3;
  border-radius: 20px;
  padding: 10px 0;
  font-size: 20px;
  font-weight: bold;
  box-shadow: 0 2px 8px rgba(33, 150, 243, 0.08);
  cursor: pointer;
  transition: all 0.3s ease;
  letter-spacing: 1px;
  font-family: "NanumSquareRoundB", sans-serif;
}

.reserve-btn-inner:hover {
  background: #f0f8ff;
  border-color: #1976d2;
  color: #1976d2;
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(33, 150, 243, 0.2);
}

.reserve-btn-inner:active {
  transform: translateY(0);
  background: #e3f2fd;
  box-shadow: 0 2px 6px rgba(33, 150, 243, 0.15);
}

/* 내 티켓 확인 버튼 스타일 */
.reserve-btn-inner.reservation-confirm {
  /* background: linear-gradient(135deg, rgba(102, 189, 255, 0.15) 0%, rgba(255, 105, 180, 0.12) 100%); */
  border: 2px solid transparent;
  background-clip: padding-box;
  position: relative;
  font-weight: 800;
  cursor: pointer;
  color: #66bdff;
  letter-spacing: 0.3px;
}

.reserve-btn-inner.reservation-confirm::before {
  content: "";
  position: absolute;
  inset: -2px;
  border-radius: inherit;
  background: linear-gradient(135deg, #66bdff, #ff69b4);
  z-index: -1;
  opacity: 0.5;
}

.reserve-btn-inner.reservation-confirm:hover {
  background: linear-gradient(135deg, rgba(102, 189, 255, 0.25) 0%, rgba(255, 105, 180, 0.2) 100%);
  transform: translateY(-2px);
  color: #ffffff;
  box-shadow: 0 4px 18px rgba(102, 189, 255, 0.25),
    0 4px 18px rgba(255, 105, 180, 0.2);
}

.reserve-btn-inner.reservation-confirm:hover::before {
  opacity: 0.7;
}

.reserve-btn-inner.reservation-confirm:active {
  transform: translateY(0);
  box-shadow: 0 2px 8px rgba(102, 189, 255, 0.18),
    0 2px 8px rgba(255, 105, 180, 0.12);
}

/* 링꾹예매 버튼 스타일 */
.reserve-btn-inner.linkook-reserve {
  background: linear-gradient(135deg, #66bdff 0%, #ff69b4 100%);
  color: white;
  border: none;
  box-shadow: 0 4px 15px rgba(102, 189, 255, 0.4),
    0 4px 15px rgba(255, 105, 180, 0.3);
  animation: linkookButtonPulse 2s ease-in-out infinite;
}

.reserve-btn-inner.linkook-reserve:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(102, 189, 255, 0.5),
    0 6px 20px rgba(255, 105, 180, 0.4);
}

.reserve-btn-inner.linkook-reserve:active {
  transform: translateY(0);
  box-shadow: 0 2px 10px rgba(102, 189, 255, 0.4),
    0 2px 10px rgba(255, 105, 180, 0.3);
}

/* 매진(완판) 버튼 스타일 - 긍정적인 톤 */
.reserve-btn-inner.sold-out {
  position: relative;
  overflow: hidden;
  background: linear-gradient(135deg, #ffd54f 0%, #ff7aa2 55%, #7cdbff 110%);
  color: #ffffff;
  border: none;
  box-shadow: 0 6px 22px rgba(255, 122, 162, 0.35),
    0 6px 22px rgba(124, 219, 255, 0.25);
  letter-spacing: 1.2px;
}

.reserve-btn-inner.sold-out::before {
  content: "";
  position: absolute;
  inset: -40% -60%;
  background: radial-gradient(circle at 30% 30%, rgba(255, 255, 255, 0.35), transparent 55%),
    radial-gradient(circle at 70% 60%, rgba(255, 255, 255, 0.25), transparent 55%),
    linear-gradient(120deg, transparent 40%, rgba(255, 255, 255, 0.35) 50%, transparent 60%);
  transform: translateX(-20%);
  animation: soldOutShine 2.4s ease-in-out infinite;
  pointer-events: none;
}

.reserve-btn-inner.sold-out:hover {
  transform: translateY(-2px);
  box-shadow: 0 10px 28px rgba(255, 122, 162, 0.45),
    0 10px 28px rgba(124, 219, 255, 0.35);
}

.reserve-btn-inner.sold-out:active {
  transform: translateY(0);
  box-shadow: 0 6px 18px rgba(255, 122, 162, 0.35),
    0 6px 18px rgba(124, 219, 255, 0.25);
}

/* 예매대기 버튼 스타일 (칙칙하지 않게 밝은 톤) */
.reserve-btn-inner.reservation-wait {
  background: linear-gradient(135deg, rgba(102, 189, 255, 0.18) 0%, rgba(255, 105, 180, 0.12) 100%);
  color: #1976d2;
  border: 2px dashed rgba(102, 189, 255, 0.7);
  box-shadow: 0 4px 14px rgba(102, 189, 255, 0.18);
  cursor: not-allowed;
  animation: none;
  transform: none;
}

.reserve-btn-inner.reservation-wait:hover,
.reserve-btn-inner.reservation-wait:active {
  transform: none;
  box-shadow: 0 4px 14px rgba(102, 189, 255, 0.18);
}

@keyframes soldOutShine {
  0% {
    transform: translateX(-22%) rotate(-3deg);
    opacity: 0.85;
  }

  50% {
    transform: translateX(10%) rotate(1deg);
    opacity: 1;
  }

  100% {
    transform: translateX(26%) rotate(3deg);
    opacity: 0.85;
  }
}

@keyframes linkookButtonPulse {

  0%,
  100% {
    box-shadow: 0 4px 15px rgba(102, 189, 255, 0.4),
      0 4px 15px rgba(255, 105, 180, 0.3);
  }

  50% {
    box-shadow: 0 6px 20px rgba(102, 189, 255, 0.6),
      0 6px 20px rgba(255, 105, 180, 0.5);
  }
}

/* 예매 종료 버튼 스타일 */
.reserve-btn-inner.reservation-closed,
.reserve-btn-inner:disabled {
  background: linear-gradient(135deg, #f5f5f5 0%, #f3f4f6 100%);
  color: #888;
  border: 1px solid #e5e7eb;
  cursor: not-allowed;
  animation: none;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  opacity: 0.8;
  pointer-events: none;
}

.reserve-btn-inner.reservation-closed:hover,
.reserve-btn-inner:disabled:hover {
  background: linear-gradient(135deg, #f5f5f5 0%, #e8e8e8 100%);
  color: #888;
  transform: none;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
}

.reserve-btn-inner.reservation-closed:active,
.reserve-btn-inner:disabled:active {
  background: linear-gradient(135deg, #f5f5f5 0%, #e8e8e8 100%);
  transform: none;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
}


.reserve-btn:not(:active) {
  background: #fff;
  transform: scale(1);
  box-shadow: 0 2px 8px rgba(33, 150, 243, 0.08);
}


/* 관리자 전용 섹션 스타일 */
.admin-section {
  width: 100%;
  border-radius: 16px;
  background: linear-gradient(135deg, #f8f9fa 0%, #fff 100%);
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.04);
  overflow: hidden;
}

.admin-section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 20px;
  cursor: pointer;
  user-select: none;
  transition: all 0.2s ease;
}

.admin-section-header:hover {
  background: rgba(0, 0, 0, 0.02);
}

.admin-section-title {
  font-size: 13px;
  font-weight: 500;
  color: #888;
  font-family: "NanumSquareRoundR", sans-serif;
  letter-spacing: -0.3px;
}

.admin-section-toggle {
  font-size: 10px;
  color: #bbb;
  transition: transform 0.3s ease;
}

.admin-section-content {
  padding: 20px 20px;
}

.admin-info-table {
  display: flex;
  flex-direction: column;
  gap: 14px;
  margin-bottom: 20px;
}

.admin-info-row {
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
}

.admin-info-label {
  width: 65px;
  min-width: 65px;
  font-size: 11px;
  font-weight: 500;
  color: #aaa;
  font-family: "NanumSquareRoundR", sans-serif;
}

.admin-info-value {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  font-size: 13px;
  color: #555;
  font-family: "NanumSquareRoundR", sans-serif;
}

.admin-sub-text {
  font-size: 11px;
  color: #aaa;
}

.admin-date-text {
  font-size: 10px;
  color: #ccc;
  margin-left: 4px;
}

.admin-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  padding-top: 16px;
  border-top: 1px solid rgba(0, 0, 0, 0.05);
}

/* 관리자 메뉴 액션 버튼 크기 통일 */
.admin-actions .edit-btn,
.admin-actions .delete-btn,
.admin-actions .reservation-manage-btn,
.admin-actions .reservation-list-btn,
.admin-actions .member-manage-btn {
  margin: 0;
  /* 기존 margin-right/bottom 무시하고 gap 사용 */
  min-height: 28px;
  padding: 6px 12px;
  border-radius: 6px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  line-height: 1;
  white-space: nowrap;
}

.approval-toggle {
  display: flex;
  align-items: center;
  gap: 8px;
}

.approval-status {
  font-size: 11px;
  padding: 4px 10px;
  border-radius: 20px;
  font-weight: 500;
  font-family: "NanumSquareRoundR", sans-serif;
  letter-spacing: -0.2px;
}

.approval-status.approved {
  background: rgba(76, 175, 80, 0.12);
  color: #43a047;
}

.approval-status.pending {
  background: rgba(255, 152, 0, 0.12);
  color: #f57c00;
}

.banner-file-status {
  font-size: 10px;
  padding: 3px 8px;
  border-radius: 12px;
  font-weight: 500;
  font-family: "NanumSquareRoundR", sans-serif;
}

.banner-file-status.has-file {
  background: rgba(33, 150, 243, 0.1);
  color: #1976d2;
}

.banner-file-status.no-file {
  background: rgba(0, 0, 0, 0.04);
  color: #999;
}

.approval-toggle-btn {
  font-size: 11px;
  padding: 5px 12px;
  border-radius: 16px;
  border: none;
  background: rgba(0, 0, 0, 0.06);
  color: #666;
  cursor: pointer;
  font-family: "NanumSquareRoundR", sans-serif;
  transition: all 0.2s ease;
}

.approval-toggle-btn:hover {
  background: rgba(0, 0, 0, 0.1);
  color: #333;
}

.approval-toggle-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

/* 찜 정보 스타일 */
.like-count-badge {
  font-size: 12px;
  padding: 4px 10px;
  border-radius: 16px;
  background: linear-gradient(135deg, rgba(255, 107, 157, 0.15) 0%, rgba(255, 107, 157, 0.08) 100%);
  color: #e91e63;
  font-weight: 500;
  font-family: "NanumSquareRoundB", sans-serif;
}

.show-liked-btn {
  font-size: 11px;
  padding: 4px 10px;
  border-radius: 12px;
  border: 1px solid #ddd;
  background: white;
  color: #666;
  cursor: pointer;
  font-family: "NanumSquareRoundR", sans-serif;
  transition: all 0.2s ease;
}

.show-liked-btn:hover {
  border-color: #e91e63;
  color: #e91e63;
  background: rgba(255, 107, 157, 0.05);
}

.show-liked-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.liked-users-row {
  align-items: flex-start !important;
}

.liked-users-list {
  flex-direction: column !important;
  align-items: flex-start !important;
  gap: 6px !important;
  max-height: 200px;
  overflow-y: auto;
  padding: 8px;
  background: rgba(0, 0, 0, 0.02);
  border-radius: 8px;
  width: 100%;
}

.liked-user-item {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 4px 0;
  width: 100%;
}

.liked-user-avatar {
  width: 24px;
  height: 24px;
  border-radius: 50%;
  object-fit: cover;
  border: 1px solid rgba(0, 0, 0, 0.1);
}

.liked-user-avatar-placeholder {
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f5f5f5;
  border-radius: 50%;
  font-size: 14px;
}

.liked-user-name {
  font-size: 12px;
  color: #333;
  font-family: "NanumSquareRoundB", sans-serif;
}

.liked-user-email {
  font-size: 10px;
  color: #999;
}

/* 관리자용 작성자/수정자/주최자 정보 스타일 */
.organizer-info,
.creator-info,
.modifier-info {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.organizer-nickname {
  font-weight: bold;
  color: #667eea;
  font-size: 16px;
}

.organizer-email {
  color: #667eea;
  font-size: 14px;
}

.creator-nickname,
.modifier-nickname {
  font-weight: bold;
  color: #333;
  font-size: 16px;
}

.creator-email,
.modifier-email {
  color: #666;
  font-size: 14px;
}

.creator-date,
.modifier-date {
  color: #999;
  font-size: 13px;
}

.overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  z-index: 10;
}

.popup {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  max-width: 70%;
  width: 70%;
  height: 90%;
  max-height: 90%;
  padding: 20px;
  background: white;
  border-radius: 8px;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.3);
  box-sizing: border-box;
  z-index: 20;
  text-align: center;
  margin: auto;
  overflow-y: scroll;
}

.likeBtnOverlay {
  position: absolute;
  bottom: 10px;
  /* 이미지 위에서의 위치 */
  right: 5px;
  /* 이미지 왼쪽에서의 위치 */
  color: black;
  /* 글자 색 */
  font-weight: bold;
  /* 글자 두께 */
  font-family: var(--font-noto-b);
  padding: 0px 5px;
  /* 배경과 글씨 사이의 여백 */
  border-radius: 5px;
  /* 배경의 모서리를 둥글게 */
  padding-bottom: 2px;
  z-index: 10;
}

.like-heart {
  padding: 5px;
  /* 배경과 글씨 사이의 여백 */
  margin: 0px 2px;
  border-radius: 15px;
  /* 배경의 모서리를 둥글게 */
  height: 30px !important;
  width: 30px !important;
  border: 1px solid #ccc;
  background-color: rgba(0, 0, 0, 0.3);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.4);
}

.like-heart.liked {
  background-color: white;
}


/* 유튜브 영상 섹션 */
.youtube-videos-grid {
  display: flex;
  flex-direction: column;
  gap: 24px;
  padding: 0 10px;
}

.youtube-video-item {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.youtube-embed-wrapper {
  position: relative;
  width: 100%;
  padding-top: 56.25%;
  /* 16:9 */
  border-radius: 12px;
  overflow: hidden;
  background: #000;
}

.youtube-embed {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  border-radius: 12px;
}

.youtube-video-meta {
  display: flex;
  flex-direction: column;
  gap: 3px;
  padding: 0 2px;
}

.youtube-video-title {
  font-family: "NanumSquareRoundB", sans-serif;
  font-size: 14px;
  color: #222;
  line-height: 1.4;
  margin: 0;
}

.youtube-video-channel {
  font-family: "NanumSquareRoundR", sans-serif;
  font-size: 13px;
  color: #868e96;
  margin: 0;
}

/* ========================================
   Small (~480px): 모바일 화면
   ======================================== */
@media screen and (max-width: 480px) {
  .popup {
    max-width: 90%;
    width: 90%;
  }

  .spinner {
    width: 40px;
    height: 40px;
    border: 4px solid #f3f3f3;
    border-top: 4px solid #66bdff;
  }

  .loading p {
    font-size: 14px;
  }

  .detail-container {
    padding: 0px 10px;
  }

  .detail-title h1 {
    font-size: 1.1rem;
  }

  .detail-more-link {
    font-size: 11px;
  }

  .back-arrow-btn {
    font-size: 18px;
    padding: 4px 8px;
  }

  .share-icon {
    width: 20px;
    height: 20px;
  }

  .edit-btn,
  .delete-btn,
  .reservation-manage-btn,
  .reservation-list-btn,
  .member-manage-btn {
    font-size: 11px;
    padding: 4px 8px;
    border-radius: 4px;
  }

  .twitter-icon {
    width: 14px;
    height: 14px;
  }

  .detail-poster-container {
    height: 300px;
    position: relative;
    overflow: hidden;
    background: #000;
  }

  .poster-blur-background {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-size: cover;
    background-position: center;
    filter: blur(4px);
    transform: scale(1.1);
    opacity: 0.3;
    z-index: 1;
  }

  .detail-poster {
    position: absolute;
    top: 33%;
    left: 42vw;
    transform: translate(-50%, -50%);
    max-width: 50%;
    max-height: 85%;
    width: auto;
    height: auto;
    border-radius: 0px;
    box-shadow: 0 6px 24px rgba(0, 0, 0, 0.3);
    z-index: 2;
  }

  .likeBtnOverlay {
    z-index: 3;
  }

  .detail-title h1 {
    font-size: 1.1rem;
  }

  .info-label {
    width: 70px;
    min-width: 70px;
    font-size: 11px;
  }

  .info-value {
    font-size: 12px;
  }

  .place-with-map {
    gap: 6px;
  }

  .map-view-link {
    font-size: 10px;
  }

  .soldout-badge {
    font-size: 10px;
    padding: 2px 6px;
  }

  .detail-like-row {
    font-size: 12px;
    gap: 4px;
  }

  .detail-like-btn {
    font-size: 11px;
    padding: 0 1px;
  }

  .detail-like-count {
    font-size: 11px;
  }

  .reserve-btn {
    font-size: 16px;
    padding: 13px 13px;
    max-width: 100%;
  }

  .approval-toggle {
    display: flex;
    align-items: center;
    gap: 10px;
  }

  .admin-section {
    margin-top: 20px;
    border-radius: 12px;
  }

  .admin-section-header {
    padding: 12px 16px;
  }

  .admin-section-title {
    font-size: 12px;
  }

  .admin-section-content {
    padding: 16px 16px;
  }

  .admin-info-label {
    width: 55px;
    min-width: 55px;
    font-size: 10px;
  }

  .admin-info-value {
    font-size: 12px;
  }

  .approval-status {
    font-size: 10px;
    padding: 3px 8px;
  }

  .banner-file-status {
    font-size: 9px;
    padding: 2px 6px;
  }

  .approval-toggle-btn {
    font-size: 10px;
    padding: 4px 10px;
  }

  .tab-button {
    padding: 10px 6px;
    font-size: 12px;
  }

  .tab-content {
    padding: 12px 6px 10px 6px;
  }

  .detail-image-section {
    margin-bottom: 20px;
  }

  .detail-image {
    border-radius: 6px;
  }

  .lineup-grid {
    grid-template-columns: repeat(1, 1fr);
    gap: 8px;
  }

  .setlist-item {
    padding: 8px 10px;
    margin-bottom: 4px;
    font-size: 12px;
  }

  .song-number {
    font-size: 12px;
    margin-right: 6px;
    min-width: 20px;
  }

  .song-title {
    font-size: 12px;
  }

  .booking-item {
    margin-bottom: 12px;
    padding: 12px;
  }

  .booking-label {
    font-size: 12px;
    margin-bottom: 6px;
  }

  .booking-value {
    font-size: 12px;
  }

  .booking-link-button {
    font-size: 12px;
    padding: 8px 16px;
  }

  .empty-content {
    padding: 30px 10px;
    font-size: 12px;
  }

  .youtube-videos-grid {
    padding: 0px 0px !important;
  }

  .detail-loading .loading-container {
    padding: 30px 15px;
    margin: 15px;
  }

  .detail-loading .loading-logo img {
    width: 100px;
  }

  .detail-loading .loading-text h2 {
    font-size: 18px;
  }

  .detail-loading .loading-text p {
    font-size: 13px;
  }

  .detail-loading .spinner {
    width: 50px;
    height: 50px;
  }

}


/* 공연 후기 작성 추천 배너 스타일 */
.review-banner {
  position: relative;
  padding: 12px 16px;
  background: linear-gradient(135deg, #e6f7ff 0%, #ffffff 100%);
  border-radius: 10px;
  border: 2px dashed #4096ff;
  cursor: pointer;
  transition: all 0.3s ease;
  overflow: hidden;
  margin: 12px auto;
  display: flex;
  align-items: center;
  justify-content: center;
  animation: reviewBannerPulse 2s ease-in-out infinite;
}

.review-banner:hover {
  transform: translateY(-2px);
  border-style: solid;
  box-shadow: 0 4px 16px rgba(64, 150, 255, 0.4);
  background: linear-gradient(135deg, #d9f0ff 0%, #f7fcff 100%);
}

.review-banner-glow {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: radial-gradient(circle at 50% 50%,
      rgba(64, 150, 255, 0.1) 0%,
      transparent 70%);
  pointer-events: none;
}

.review-banner-content {
  position: relative;
  z-index: 1;
  display: flex;
  align-items: center;
  gap: 12px;
  text-align: left;
}

.review-icon-circle {
  width: 45px;
  height: 45px;
  border-radius: 50%;
  background: linear-gradient(135deg, #4096ff 0%, #1677ff 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 2px 8px rgba(64, 150, 255, 0.3);
  animation: reviewIconBounce 2s ease-in-out infinite;
  flex-shrink: 0;
}

.review-icon {
  font-size: 22px;
  animation: reviewIconRotate 3s ease-in-out infinite;
}

.review-text {
  display: flex;
  flex-direction: column;
  gap: 4px;
  flex: 1;
}

.review-title {
  margin: 0;
  font-size: 0.85rem;
  font-weight: bold;
  color: #333;
  font-family: "NanumSquareRoundB", sans-serif;
}

.review-subtitle {
  margin: 0;
  font-size: 0.7rem;
  color: #666;
  font-family: "NanumSquareRoundR", sans-serif;
  line-height: 1.3;
}

.review-badge {
  display: inline-block;
  background: linear-gradient(135deg, #4096ff 0%, #1677ff 100%);
  color: white;
  padding: 2px 8px;
  border-radius: 8px;
  font-size: 0.65rem;
  font-weight: bold;
  font-family: "NanumSquareRoundB", sans-serif;
  box-shadow: 0 2px 4px rgba(64, 150, 255, 0.3);
  margin-top: 2px;
  width: fit-content;
}

@keyframes reviewBannerPulse {

  0%,
  100% {
    box-shadow: 0 2px 10px rgba(64, 150, 255, 0.2);
  }

  50% {
    box-shadow: 0 4px 20px rgba(64, 150, 255, 0.4);
  }
}

@keyframes reviewIconBounce {

  0%,
  100% {
    transform: translateY(0);
  }

  50% {
    transform: translateY(-4px);
  }
}

@keyframes reviewIconRotate {

  0%,
  100% {
    transform: rotate(0deg);
  }

  25% {
    transform: rotate(-5deg);
  }

  75% {
    transform: rotate(5deg);
  }
}
</style>
